import { NodeSelection } from './../../selection/index';
import { NodeCutter } from './nodecutter';
import * as CONSTANT from './../base/constant';
import { detach, Browser, isNullOrUndefined as isNOU, createElement, closest } from '@syncfusion/ej2-base';
import { InsertMethods } from './insert-methods';
import { updateTextNode, nestedListCleanUp, scrollToCursor, cleanHTMLString } from './../../common/util';
/**
 * This InsertHtml class contains methods to insert HTML nodes or text into a document.
 *
 * @hidden
 * @private
 */
export class InsertHtml {
    /**
     * Inserts an HTML node or text into the specified document.
     *
     * @param {Document} docElement - The document where the node should be inserted.
     * @param {Node | string} insertNode - The node or text to be inserted. Can be a DOM Node or a string representing HTML.
     * @param {Element} [editNode] - The container or editor node where the insertion will occur.
     * @param {boolean} [isExternal] - Flag indicating if the node is from an external source. Optional.
     * @param {string} [enterAction] - Represents the action taken when 'Enter' is pressed. Optional.
     * @param {EditorManager} [editorManager] - Represents the EditorManager instance. Optional.
     * @returns {void}
     * @hidden
     * @private
     */
    static Insert(docElement, insertNode, editNode, isExternal, enterAction, editorManager) {
        const insertedNode = this.prepareInsertNode(insertNode, isExternal, editNode);
        // Capture Blazor mode from the EditorManager for util functions
        this.isBlazor = !isNOU(editorManager) ? editorManager.isBlazor : false;
        const scrollHeight = !isNOU(editNode) ? editNode.scrollHeight : 0;
        const nodeSelection = new NodeSelection(editNode);
        const nodeCutter = new NodeCutter();
        let range = nodeSelection.getRange(docElement);
        //Adjusts the selection range to handle various edge cases for cursor positioning
        range = this.adjustSelectionRange(nodeSelection, docElement, editNode, range);
        const isCursor = this.isCursorAtStartPoint(range);
        const isCollapsed = range.collapsed;
        const nodes = this.getNodeCollection(range, nodeSelection, insertedNode);
        const isInsertedNodeTable = insertedNode.nodeName.toLowerCase() === 'table';
        let closestParentNode = this.findRelevantParentNode(nodes, isInsertedNodeTable, range, editNode);
        // Handle BR parent case
        if (closestParentNode && closestParentNode.nodeName === 'BR') {
            closestParentNode = closestParentNode.parentNode;
        }
        else if (this.isStartContainerMediaEle(nodes[0])) {
            const mediaParent = this.isStartContainerMediaEle(nodes[0]).parentElement;
            if (mediaParent && mediaParent.nodeName !== 'LI') {
                closestParentNode = mediaParent;
            }
        }
        // Handling the table insertion inside list items
        if (closestParentNode && closestParentNode.nodeName === 'LI' && isInsertedNodeTable) {
            this.handleTableInListItem(range, insertedNode, closestParentNode, nodes, nodeSelection, nodeCutter, editNode);
            return;
        }
        // Handle image insertion at empty cursor position
        const isImgOnlyNode = insertedNode.nodeName !== '#text' &&
            !isNOU(insertedNode.children[0]) &&
            !isNOU(insertedNode.children[0].tagName) &&
            insertedNode.children[0].tagName === 'IMG' &&
            insertedNode.children.length === 1;
        const isEmptyCursorPosition = isCursor &&
            range.startContainer.textContent === '' &&
            range.startContainer.nodeName !== 'BR' &&
            enterAction !== 'BR';
        if (isEmptyCursorPosition && isImgOnlyNode) {
            range.startContainer.innerHTML = '';
        }
        const isPasteContentOrInsertHtml = isExternal || (!isNOU(insertedNode) &&
            !isNOU(insertedNode.classList) &&
            insertedNode.classList.contains('pasteContent'));
        const targetCells = docElement.querySelectorAll('td.e-cell-select, th.e-cell-select');
        if (targetCells && targetCells.length > 1) {
            this.clearTargetCells(targetCells);
        }
        if (isPasteContentOrInsertHtml) {
            if (editorManager &&
                editorManager.tableObj &&
                editorManager.tableObj.tablePastingObj) {
                const tablePastingObj = editorManager.tableObj.tablePastingObj;
                const insertedTable = tablePastingObj.getValidTableFromPaste(insertedNode);
                const hasSelectedTargetCells = targetCells && targetCells.length > 0;
                if (hasSelectedTargetCells && insertedTable) {
                    // Delegate to the table pasting logic
                    tablePastingObj.handleTablePaste(insertedTable, targetCells);
                    return;
                }
            }
            this.pasteInsertHTML(nodes, insertedNode, range, nodeSelection, nodeCutter, docElement, isCollapsed, closestParentNode, editNode, enterAction);
            return;
        }
        if (this.shouldInsertOutsideRange(editNode, range, isCollapsed, closestParentNode, insertedNode)) {
            this.handleContentInsertionOutsideRange(docElement, editNode, range, nodeSelection, nodeCutter, isCollapsed, closestParentNode, insertedNode, nodes, insertNode, isCursor, enterAction);
        }
        else {
            this.handleContentInsertionInsideRange(docElement, range, nodeSelection, nodeCutter, closestParentNode, insertedNode, isCursor);
        }
        // Scroll to cursor if needed for the image
        if (this.shouldScrollToCursor(editNode, scrollHeight, insertedNode)) {
            scrollToCursor(docElement, editNode);
        }
    }
    /*
    * Clears the content of all target cells by setting their innerHTML to a line break
    */
    static clearTargetCells(cells) {
        for (let i = 0; i < cells.length; i++) {
            cells[i].innerHTML = '<br>';
        }
    }
    // Prepares the node or HTML string for insertion, attaching it to a temporary container if necessary, and ensuring valid usage.
    static prepareInsertNode(insertNode, isExternal, editNode) {
        if (typeof insertNode === 'string') {
            insertNode = cleanHTMLString(insertNode, editNode);
            const divNode = createElement('div');
            divNode.innerHTML = insertNode.replace(/&(times|divide|ne)(;?)/g, '&amp;$1$2');
            return isExternal ? divNode : divNode.firstChild;
        }
        else {
            const isValidPasteContent = !isNOU(insertNode) &&
                !isNOU(insertNode.classList) &&
                insertNode.classList.contains('pasteContent');
            if (isExternal && !isValidPasteContent) {
                const divNode = createElement('div');
                divNode.appendChild(insertNode);
                return divNode;
            }
            else {
                if (Browser.userAgent.indexOf('Firefox') !== -1) {
                    this.unwrapSpansAroundBlocks(insertNode);
                }
                return insertNode;
            }
        }
    }
    //Unwraps span elements that contain block-level elements within them.
    static unwrapSpansAroundBlocks(node) {
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }
        const element = node;
        // Recursively process child nodes first (bottom-up traversal)
        Array.from(element.childNodes).forEach((child) => {
            this.unwrapSpansAroundBlocks(child);
        });
        if (element.tagName.toLowerCase() === 'span' && element.hasChildNodes()) {
            // Define a CSS selector for common block-level elements
            const blockSelectors = 'address, article, aside, blockquote, canvas, dd, div, dl, dt, ' +
                'fieldset, figcaption, figure, footer, form, h1, h2, h3, h4, h5, h6, ' +
                'header, hr, li, main, nav, noscript, ol, p, pre, section, table, tfoot, ul, video';
            // Check if there is any block-level descendant
            const hasBlockDescendant = element.querySelector(blockSelectors) !== null;
            if (hasBlockDescendant && element.parentNode) {
                while (element.firstChild) {
                    element.parentNode.insertBefore(element.firstChild, element);
                }
                element.parentNode.removeChild(element);
            }
        }
    }
    // Adjusts the selection range to handle various edge cases for cursor positioning.
    static adjustSelectionRange(nodeSelection, docElement, editNode, range) {
        // Check if this is a collapsed selection at the beginning (offset 0)
        const isCollapsedAtStart = range.startContainer === range.endContainer &&
            range.startOffset === 0 && range.startOffset === range.endOffset;
        if (!isCollapsedAtStart) {
            return range; // Early return if not a collapsed selection at start
        }
        // Apply each adjustment in based on the cursor range.
        range = this.adjustEmptyEditorSelection(nodeSelection, docElement, editNode, range);
        range = this.adjustSelectionToFirstTextNode(nodeSelection, docElement, editNode, range);
        range = this.adjustBrElementSelection(nodeSelection, docElement, range);
        return range;
    }
    // Adjusts selection when the editor is empty with a single block element.
    static adjustEmptyEditorSelection(nodeSelection, docElement, editNode, range) {
        if (range.startContainer === editNode &&
            editNode.textContent.length === 0 &&
            (editNode.children[0].tagName === 'P' ||
                editNode.children[0].tagName === 'DIV' ||
                editNode.children[0].tagName === 'BR')) {
            nodeSelection.setSelectionText(docElement, range.startContainer.children[0], range.startContainer.children[0], 0, 0);
            return nodeSelection.getRange(docElement);
        }
        return range;
    }
    // Adjusts selection to the first text node when cursor is at the start of content.
    static adjustSelectionToFirstTextNode(nodeSelection, docElement, editNode, range) {
        if (range.startContainer === editNode &&
            editNode.textContent.trim().length > 0 && editNode.childNodes[0].tagName !== 'TABLE') {
            const focusNode = this.findFirstTextNode(range.startContainer);
            if (!isNOU(focusNode)) {
                nodeSelection.setSelectionText(docElement, focusNode, focusNode, 0, 0);
                return nodeSelection.getRange(docElement);
            }
        }
        return range;
    }
    // Adjusts selection when cursor is on a BR element
    static adjustBrElementSelection(nodeSelection, docElement, range) {
        if (range.startContainer.nodeName === 'BR') {
            const currentIndex = Array.prototype.slice.call(range.startContainer.parentElement.childNodes).indexOf(range.startContainer);
            nodeSelection.setSelectionText(docElement, range.startContainer.parentElement, range.startContainer.parentElement, currentIndex, currentIndex);
            return nodeSelection.getRange(docElement);
        }
        return range;
    }
    // Handles the insertion of a table element within a list item context.
    static handleTableInListItem(range, insertedNode, closestParentNode, nodes, nodeSelection, nodeCutter, editNode) {
        if (nodes.length === 0) {
            const tableCursor = nodeSelection.processedTableImageCursor(range);
            if (tableCursor.startName === 'TABLE' || tableCursor.endName === 'TABLE') {
                const tableNode = tableCursor.start ? tableCursor.startNode : tableCursor.endNode;
                nodes.push(tableNode);
            }
        }
        const lastClosestParentNode = this.findClosestRelevantElement(nodes[nodes.length - 1].parentNode, editNode);
        this.insertTableInList(range, insertedNode, closestParentNode, nodes[0], nodeCutter, lastClosestParentNode);
    }
    // Determines if the cursor is positioned at the start of the range.
    static isCursorAtStartPoint(range) {
        return range.startOffset === 0 && range.startOffset === range.endOffset &&
            range.startContainer === range.endContainer;
    }
    // Identifies the most contextually relevant parent node for insertion based on various criteria.
    static findRelevantParentNode(nodes, isInsertedNodeTable, range, editNode) {
        if (isInsertedNodeTable) {
            return (!isNOU(nodes[0]) && !isNOU(nodes[0].parentNode)) ?
                this.findClosestRelevantElement(nodes[0].parentNode, editNode) : range.startContainer;
        }
        else {
            return nodes[0];
        }
    }
    // Checks if the content should be inserted outside the existing selection range based on multiple checks.
    static shouldInsertOutsideRange(editNode, range, isCollapsed, closestParentNode, insertedNode) {
        return editNode !== range.startContainer && ((!isCollapsed && !(closestParentNode.nodeType === Node.ELEMENT_NODE &&
            CONSTANT.TABLE_BLOCK_TAGS.indexOf(closestParentNode.tagName.toLocaleLowerCase()) !== -1))
            || (insertedNode.nodeName.toLowerCase() === 'table' && closestParentNode &&
                CONSTANT.TABLE_BLOCK_TAGS.indexOf(closestParentNode.tagName.toLocaleLowerCase()) === -1));
    }
    // Handles insertion of content outside the specified selection range, managing complex cases including tables.
    static handleContentInsertionOutsideRange(docElement, editNode, range, nodeSelection, nodeCutter, isCollapsed, closestParentNode, insertedNode, nodes, insertNode, isCursor, enterAction) {
        // Extract content and prepare for insertion
        const preNode = nodeCutter.GetSpliceNode(range, closestParentNode);
        const sibNode = preNode.previousSibling;
        const parentNode = preNode.parentNode;
        // Update selection based on node structure
        if (nodes.length === 1) {
            nodeSelection.setSelectionContents(docElement, preNode);
            range = nodeSelection.getRange(docElement);
        }
        else if (parentNode && parentNode.nodeName !== 'LI') {
            let lasNode = nodeCutter.GetSpliceNode(range, nodes[nodes.length - 1].parentElement);
            lasNode = isNOU(lasNode) ? preNode : lasNode;
            if (lasNode.nodeType === 1) {
                const lasElem = lasNode;
                const tagName = lasElem.tagName.toLowerCase();
                // If the splice node is a media element, prefer its wrapper element
                if (tagName === 'video') {
                    const wrap = lasElem.closest('.e-video-wrap');
                    lasNode = wrap;
                }
                else if (tagName === 'audio') {
                    const wrap = lasElem.closest('.e-audio-wrap');
                    lasNode = wrap;
                }
            }
            nodeSelection.setSelectionText(docElement, preNode, lasNode, 0, (lasNode.nodeType === 3) ? lasNode.textContent.length : lasNode.childNodes.length);
            range = nodeSelection.getRange(docElement);
        }
        // Extract content or clean up nested lists
        this.extractOrCleanupContent(range, parentNode);
        // Handle table insertion specially
        if (insertNode.tagName === 'TABLE') {
            this.cleanupForTableInsertion(range, editNode);
        }
        // Remove original nodes after processing
        this.removeOriginalNodes(nodes);
        // Insert node at appropriate location
        this.insertNodeAtLocation(docElement, sibNode, parentNode, editNode, insertedNode, preNode, insertNode, isCursor, range, enterAction);
        this.removeEmptyElements(editNode);
        if (insertedNode.nodeName === '#text' &&
            insertedNode.textContent.trim() === '' && insertedNode.textContent.length !== 0) {
            nodeSelection.setCursorPoint(docElement, insertedNode, insertedNode.textContent.length);
        }
        else {
            this.setSelectionAfterInsertion(insertedNode, nodeSelection, docElement);
        }
    }
    // Extracts content or cleans nested lists as required when managing inserts in outer content ranges.
    static extractOrCleanupContent(range, parentNode) {
        if (range.startContainer.parentElement.closest('ol,ul') !== null &&
            range.endContainer.parentElement.closest('ol,ul') !== null) {
            nestedListCleanUp(range, parentNode);
        }
        else {
            range.extractContents();
        }
    }
    // Performs cleanup operations necessary specifically for cases involving table insertions.
    static cleanupForTableInsertion(range, editNode) {
        const emptyElement = closest(range.startContainer, 'blockquote');
        if (!isNOU(emptyElement) && emptyElement.childNodes.length > 0) {
            for (let i = emptyElement.childNodes.length - 1; i >= 0; i--) {
                const currentChild = emptyElement.childNodes[i];
                if (!isNOU(currentChild) && currentChild.innerText.trim() === '') {
                    detach(currentChild);
                }
            }
        }
        this.removeEmptyElements(editNode, false, emptyElement);
    }
    // Removes the original nodes from the document tree after processing insertion operations.
    static removeOriginalNodes(nodes) {
        for (let index = 0; index < nodes.length; index++) {
            if (nodes[index].nodeType !== 3 && nodes[index].parentNode != null) {
                if (nodes[index].nodeName === 'IMG') {
                    continue;
                }
                nodes[index].parentNode.removeChild(nodes[index]);
            }
        }
    }
    // Directly inserts the node at a calculated location, ensuring appropriate context and order.
    static insertNodeAtLocation(docElement, sibNode, parentNode, editNode, insertedNode, preNode, insertNode, isCursor, range, enterAction) {
        if (!isNOU(sibNode) && !isNOU(sibNode.parentNode)) {
            if (docElement.contains(sibNode)) {
                InsertMethods.AppendBefore(insertedNode, sibNode, true);
            }
            else {
                range.insertNode(insertedNode);
            }
        }
        else {
            parentNode = this.findAppropriateParentNode(parentNode, editNode);
            this.insertNodeBasedOnContext(parentNode, editNode, insertedNode, insertNode, isCursor, range, preNode, enterAction);
        }
    }
    // Identifies an appropriate parent node which accommodates the insertion effectively.
    static findAppropriateParentNode(parentNode, editNode) {
        let previousNode = null;
        while (parentNode !== editNode && parentNode.firstChild &&
            (parentNode.textContent.trim() === '') && parentNode.nodeName !== 'LI') {
            const parentNode1 = parentNode.parentNode;
            previousNode = parentNode;
            parentNode = parentNode1;
        }
        return previousNode !== null ? previousNode : parentNode;
    }
    // Inserts nodes by considering established contexts like sibling nodes and nested elements.
    static insertNodeBasedOnContext(parentNode, editNode, insertedNode, insertNode, isCursor, range, preNode, enterAction) {
        if (parentNode.firstChild && (parentNode !== editNode ||
            (insertedNode.nodeName === 'TABLE' && isCursor && parentNode === range.startContainer &&
                parentNode === range.endContainer))) {
            if (parentNode.textContent.trim() === '' && parentNode !== editNode && parentNode.nodeName === 'LI') {
                parentNode.appendChild(insertedNode);
            }
            else if (parentNode.textContent.trim() === '' && parentNode !== editNode) {
                if (parentNode.parentNode && parentNode.parentNode === editNode
                    && !this.isBlockElement(insertedNode) && !(enterAction && enterAction.toUpperCase() === 'BR')) {
                    const blockNode = enterAction && enterAction.toUpperCase() === 'DIV' ? createElement('div') : createElement('p');
                    blockNode.appendChild(insertedNode);
                    InsertMethods.AppendBefore(blockNode, parentNode, false);
                }
                else {
                    InsertMethods.AppendBefore(insertedNode, parentNode, false);
                }
                detach(parentNode);
            }
            else {
                InsertMethods.AppendBefore(insertedNode, parentNode.firstChild, false);
            }
        }
        else if (isNOU(preNode.previousSibling) && insertNode.tagName === 'TABLE') {
            parentNode.prepend(insertedNode);
        }
        else {
            parentNode.appendChild(insertedNode);
        }
    }
    // Configures the node selection state after executing the insertion operation.
    static setSelectionAfterInsertion(insertedNode, nodeSelection, docElement) {
        if (insertedNode.nodeName === 'IMG') {
            this.mediaFocus(insertedNode, nodeSelection, docElement);
        }
        else if (insertedNode.nodeType !== 3) {
            nodeSelection.setSelectionText(docElement, insertedNode, insertedNode, 0, insertedNode.childNodes.length);
        }
        else {
            nodeSelection.setSelectionText(docElement, insertedNode, insertedNode, 0, insertedNode.textContent.length);
        }
    }
    // Manages insertion operations when nodes are intended to be placed within the current range selection.
    static handleContentInsertionInsideRange(docElement, range, nodeSelection, nodeCutter, closestParentNode, insertedNode, isCursor) {
        const liElement = !isNOU(closestParentNode) ?
            closest(closestParentNode, 'li') : null;
        if (this.shouldInsertInTableCell(closestParentNode, liElement, isCursor)) {
            range.extractContents();
            liElement.appendChild(insertedNode);
            this.removeEmptyNextLI(liElement);
        }
        else {
            this.insertWithRangeHandling(docElement, range, nodeCutter, insertedNode, isCursor);
        }
        this.setCursorAfterInsertion(docElement, insertedNode, nodeSelection);
    }
    // Determines if content should be inserted inside a table cell based on the specific conditions.
    static shouldInsertInTableCell(closestParentNode, liElement, isCursor) {
        return (!isNOU(closestParentNode) &&
            (closestParentNode.nodeName === 'TD' || closestParentNode.nodeName === 'TH')) &&
            !isNOU(liElement) && !isCursor;
    }
    // Handles direct node insertions by accounting for document structure and browser compatibility factors.
    static insertWithRangeHandling(docElement, range, nodeCutter, insertedNode, isCursor) {
        range.deleteContents();
        if (isCursor && range.startContainer.textContent === '' && range.startContainer.nodeName !== 'BR') {
            range.startContainer.innerHTML = '';
        }
        if (Browser.isIE) {
            const frag = docElement.createDocumentFragment();
            frag.appendChild(insertedNode);
            range.insertNode(frag);
        }
        else if (this.isHrElement(range)) {
            this.insertAfterHrElement(range, insertedNode);
        }
        else {
            this.insertBasedOnStartContainer(range, insertedNode, nodeCutter);
        }
    }
    // Handles direct node insertions by accounting for document structure and browser compatibility factors.
    static isHrElement(range) {
        return range.startContainer.nodeType === 1 &&
            range.startContainer.nodeName.toLowerCase() === 'hr' &&
            range.endContainer.nodeName.toLowerCase() === 'hr';
    }
    // Handling inserting after horizontal rule elements.
    static insertAfterHrElement(range, insertedNode) {
        const paraElem = range.startContainer.nextElementSibling;
        if (paraElem) {
            if (paraElem.querySelector('br')) {
                detach(paraElem.querySelector('br'));
            }
            paraElem.appendChild(insertedNode);
        }
    }
    // Inserts content based on the start container properties and current text structure.
    static insertBasedOnStartContainer(range, insertedNode, nodeCutter) {
        let container = range.startContainer;
        if (range.startContainer.nodeName === 'BR') {
            range.startContainer.parentElement.insertBefore(insertedNode, range.startContainer);
        }
        else if (insertedNode.nodeName === 'TABLE' && (CONSTANT.ALLOWED_TABLE_BLOCK_TAGS.indexOf(container.nodeName.toLowerCase()) === -1
            && CONSTANT.TABLE_BLOCK_TAGS.indexOf(container.nodeName.toLowerCase()) === -1)) {
            while ((CONSTANT.ALLOWED_TABLE_BLOCK_TAGS.indexOf(container.parentNode.nodeName.toLowerCase()) === -1
                && CONSTANT.TABLE_BLOCK_TAGS.indexOf(container.parentNode.nodeName.toLowerCase()) === -1)) {
                container = container.parentNode;
            }
            // Case 1: If cursor is at the start of the inline element
            if (range.startOffset === 0 && range.endOffset === 0 && container) {
                container.parentNode.insertBefore(insertedNode, container);
                const hasText = container.textContent.length > 0;
                const hasMediaElements = container.nodeName !== '#text' && container.querySelector('img, video, audio, table') !== null;
                const hasContent = hasText || hasMediaElements;
                if (!hasContent) {
                    detach(container);
                }
            }
            else if (range.startOffset === range.startContainer.textContent.length && range.endOffset ===
                range.startContainer.textContent.length &&
                container && container.parentNode) {
                // Case 2: If cursor is at the end of the inline element
                const nextSibling = container.nextSibling;
                container.parentNode.insertBefore(insertedNode, nextSibling);
            }
            else {
                // Case 3: Handling middle insertion
                const spliceNode = nodeCutter.GetSpliceNode(range, container);
                const nextNode = spliceNode.nextSibling;
                container.parentNode.insertBefore(insertedNode, nextNode);
                detach(spliceNode);
            }
        }
        else {
            range.insertNode(insertedNode);
        }
    }
    // Sets the cursor position after completing the content insertion logic.
    static setCursorAfterInsertion(docElement, insertedNode, nodeSelection) {
        if (insertedNode.nodeType !== 3 && insertedNode.childNodes.length > 0) {
            nodeSelection.setSelectionText(docElement, insertedNode, insertedNode, 1, 1);
        }
        else if (insertedNode.nodeName === 'IMG') {
            this.mediaFocus(insertedNode, nodeSelection, docElement);
        }
        else if (insertedNode.nodeType !== 3) {
            nodeSelection.setSelectionContents(docElement, insertedNode);
        }
        else {
            nodeSelection.setSelectionText(docElement, insertedNode, insertedNode, insertedNode.textContent.length, insertedNode.textContent.length);
        }
    }
    // Checks whether the editor should scroll to the cursor position after insertion.
    static shouldScrollToCursor(editNode, scrollHeight, insertedNode) {
        return !isNOU(editNode) &&
            scrollHeight < editNode.scrollHeight &&
            insertedNode.nodeType === 1 &&
            (insertedNode.nodeName === 'IMG' || !isNOU(insertedNode.querySelector('img')));
    }
    // Removes empty list items from the associated list after node insertions.
    static removeEmptyNextLI(liElement) {
        // Find the root-level list containing this list item
        let rootList = closest(liElement, 'ul,ol');
        // Navigate to the topmost list if this is inside nested lists
        while (rootList && rootList.parentElement && rootList.parentElement.nodeName === 'LI') {
            rootList = closest(rootList.parentElement, 'ul,ol');
        }
        if (!rootList) {
            return;
        }
        // Collect all list items in the list
        const listItems = rootList.querySelectorAll('li');
        // Define a helper to check if a list item is empty (no text and no media elements)
        const isEmptyListItem = (item) => {
            return item.textContent.trim() === '' &&
                !item.querySelector('audio,video,img,table,br');
        };
        // Remove all empty list items
        listItems.forEach((item) => {
            if (isEmptyListItem(item)) {
                detach(item);
            }
        });
    }
    // Recursively searches for and returns the first text node within the specified node.
    static findFirstTextNode(node) {
        if (node.nodeType === Node.TEXT_NODE) {
            return node;
        }
        for (let i = 0; i < node.childNodes.length; i++) {
            const textNode = this.findFirstTextNode(node.childNodes[i]);
            if (!isNOU(textNode)) {
                return textNode;
            }
        }
        return null;
    }
    // Handles HTML content pasting operations & insertHTML execCommand while ensuring context-specific adjustments.
    static pasteInsertHTML(nodes, insertedNode, range, nodeSelection, nodeCutter, docElement, isCollapsed, closestParentNode, editNode, enterAction) {
        const blockElement = this.getImmediateBlockNode(nodes[nodes.length - 1], editNode);
        if (blockElement && blockElement.textContent.length === 0) {
            const brElement = blockElement.querySelector('br:last-of-type');
            if (brElement) {
                brElement.classList.add('rte-temp-br');
            }
        }
        // Initialize key variables and adjust range if needed
        const isCursor = range.startOffset === range.endOffset && range.startContainer === range.endContainer;
        range = this.adjustRangeForEmptyEditor(nodes, range, nodeSelection, docElement, editNode, isCursor);
        // Setup variables for range manipulation
        const rangeInfo = this.setupRangeForPaste(nodes, insertedNode, range, nodeSelection, nodeCutter, docElement, isCollapsed, closestParentNode, editNode);
        range = rangeInfo.range;
        this.listStyleCleanup(insertedNode);
        // Process based on content structure
        const containsBlockNode = this.containsBlockElements(insertedNode);
        let processBlockElement = (blockElement && blockElement.parentElement && blockElement.parentElement.nodeName === 'LI') ? blockElement.parentElement : blockElement;
        if (!isNOU(processBlockElement) && processBlockElement.nodeName === 'LI') {
            while (processBlockElement.parentElement && (processBlockElement.parentElement.nodeName === 'LI' || processBlockElement.parentElement.nodeName === 'OL' || processBlockElement.parentElement.nodeName === 'UL')) {
                processBlockElement = processBlockElement.parentElement;
            }
            if (processBlockElement && (processBlockElement.nodeName === 'OL' || processBlockElement.nodeName === 'UL')) {
                processBlockElement.classList.add('e-rte-copy-list');
            }
        }
        else {
            insertedNode.childNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    const childElement = node;
                    if (childElement.tagName === 'OL' || childElement.tagName === 'UL') {
                        childElement.classList.add('e-rte-copy-list');
                    }
                }
            });
        }
        const lastSelectionNode = containsBlockNode
            ? this.handleBlockNodeContent(nodes, insertedNode, range, nodeCutter, editNode, enterAction, isCollapsed)
            : this.handleInlineContent(nodes, insertedNode, range, nodeSelection, docElement, editNode, isCursor, rangeInfo.sibNode, rangeInfo.lasNode, rangeInfo.isSingleNode);
        // Process special cases
        const processedNode = this.processSpecialNodes(lastSelectionNode, insertedNode, enterAction);
        // Position cursor appropriately
        this.positionCursorAfterPaste(processedNode, insertedNode, nodeSelection, docElement, editNode, enterAction);
        // Final cleanup
        this.alignCheck(editNode);
        this.listCleanUp(nodeSelection, docElement);
        this.removeEmptyBrFromParagraph(editNode);
    }
    // Cleans up inline styles applied to list items within the inserted content.
    static listStyleCleanup(node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
            const listItems = node.querySelectorAll('li');
            listItems.forEach((li) => {
                if (li.style.display === 'block') {
                    li.style.removeProperty('display');
                    if (li.getAttribute('style') === '') {
                        li.removeAttribute('style');
                    }
                }
            });
        }
    }
    // Clean up unnecessary line breaks after paste actions.
    static removeEmptyBrFromParagraph(editNode) {
        const tempBr = editNode.querySelector('br.rte-temp-br');
        if (tempBr) {
            tempBr.remove();
        }
    }
    // Adjusts range settings when the editor is empty, covering cursor initialization aspects.
    static adjustRangeForEmptyEditor(nodes, range, nodeSelection, docElement, editNode, isCursor) {
        if (isCursor && range.startContainer === editNode &&
            editNode.textContent === '' && range.startOffset === 0 && range.endOffset === 0 && editNode.childNodes[0].tagName !== 'TABLE') {
            const currentBlockNode = this.getImmediateBlockNode(nodes[nodes.length - 1], editNode);
            nodeSelection.setSelectionText(docElement, currentBlockNode, currentBlockNode, 0, 0);
            return nodeSelection.getRange(docElement);
        }
        return range;
    }
    // Sets up parameters involving range, sibling nodes, and relevant options for pasting operations.
    static setupRangeForPaste(nodes, insertedNode, range, nodeSelection, nodeCutter, docElement, isCollapsed, closestParentNode, editNode) {
        let preNode;
        let sibNode;
        let lasNode;
        let isSingleNode = false;
        if (editNode !== range.startContainer &&
            ((!isCollapsed && !(closestParentNode.nodeType === Node.ELEMENT_NODE &&
                CONSTANT.TABLE_BLOCK_TAGS.indexOf(closestParentNode.tagName.toLocaleLowerCase()) !== -1))
                || (insertedNode.nodeName.toLowerCase() === 'table' && closestParentNode &&
                    CONSTANT.TABLE_BLOCK_TAGS.indexOf(closestParentNode.tagName.toLocaleLowerCase()) === -1)) && insertedNode.firstChild.nodeName !== 'HR') {
            preNode = nodeCutter.GetSpliceNode(range, closestParentNode);
            if (!isNOU(preNode)) {
                sibNode = isNOU(preNode.previousSibling) ?
                    preNode.parentNode.previousSibling : preNode.previousSibling;
                if (nodes.length === 1) {
                    nodeSelection.setSelectionContents(docElement, preNode);
                    range = nodeSelection.getRange(docElement);
                    isSingleNode = true;
                }
                else {
                    const textContent = nodes[nodes.length - 1].textContent ? nodes[nodes.length - 1].textContent : '';
                    lasNode = nodeCutter.GetSpliceNode(range, nodes[nodes.length - 1].parentElement);
                    if (lasNode && lasNode.nodeName === 'LI' && lasNode.nextSibling && lasNode.nextSibling.nodeName === 'LI') {
                        this.isAnotherLiFromEndLi = textContent === lasNode.textContent ? false : true;
                    }
                    lasNode = isNOU(lasNode) ? preNode : lasNode;
                    nodeSelection.setSelectionText(docElement, preNode, lasNode, 0, (lasNode.nodeType === 3) ? lasNode.textContent.length : lasNode.childNodes.length);
                    range = nodeSelection.getRange(docElement);
                    isSingleNode = false;
                }
            }
        }
        // Clean node content
        this.removingComments(insertedNode);
        return { preNode, sibNode, lasNode, isSingleNode, range };
    }
    // Examines whether the inserted node contains block element.
    static containsBlockElements(insertedNode) {
        const allChildNodes = insertedNode.childNodes;
        for (let i = 0; i < allChildNodes.length; i++) {
            if (CONSTANT.BLOCK_TAGS.indexOf(allChildNodes[i].nodeName.toLowerCase()) >= 0) {
                return true;
            }
        }
        return false;
    }
    // Processes inline-only content during paste operations for correct insertion.
    static handleInlineContent(nodes, insertedNode, range, nodeSelection, docElement, editNode, isCursor, sibNode, lasNode, isSingleNode) {
        const fragment = document.createDocumentFragment();
        if (!isCursor) {
            return this.handleRegularInlineContent(insertedNode, range, fragment, editNode, sibNode, lasNode, isSingleNode);
        }
        else {
            return this.handleCursorInlineContent(nodes, insertedNode, range, nodeSelection, docElement, editNode, fragment);
        }
    }
    // Handles paste operations when dealing with non-collapsed inline selections.
    static handleRegularInlineContent(insertedNode, range, fragment, editNode, sibNode, lasNode, isSingleNode) {
        let lastSelectionNode;
        while (insertedNode.firstChild) {
            lastSelectionNode = insertedNode.firstChild;
            fragment.appendChild(insertedNode.firstChild);
        }
        if (isSingleNode) {
            range.deleteContents();
            this.removeEmptyElements(editNode, true);
            range.insertNode(fragment);
        }
        else {
            const startContainerParent = editNode === range.startContainer ?
                range.startContainer : range.startContainer.parentNode;
            const startIndex = Array.prototype.indexOf.call(startContainerParent.childNodes, (Browser.userAgent.indexOf('Firefox') !== -1 && editNode === range.startContainer) ?
                range.startContainer.firstChild : range.startContainer);
            range.deleteContents();
            if (startIndex !== -1) {
                range.setStart(startContainerParent, startIndex);
                range.setEnd(startContainerParent, startIndex);
            }
            if (!isNOU(lasNode) && lasNode !== editNode &&
                editNode.childNodes.length > 0 && editNode.childNodes[0] !== lasNode) {
                detach(lasNode);
                this.removeEmptyElements(editNode, true);
            }
            if (!isNOU(sibNode) && sibNode.parentNode !== editNode.parentNode) {
                if (sibNode.parentNode === editNode) {
                    sibNode.appendChild(fragment);
                }
                else {
                    sibNode.parentNode.appendChild(fragment);
                }
            }
            else {
                range.insertNode(fragment);
            }
        }
        return lastSelectionNode;
    }
    //To return any media element in range startContainer
    static isStartContainerMediaEle(startElem) {
        if (!startElem) {
            return null;
        }
        startElem = startElem.nodeType === Node.ELEMENT_NODE ?
            startElem : startElem.parentElement;
        const mediaEle = startElem.closest('img, .e-video-wrap, .e-embed-video-wrap, .e-audio-wrap');
        if (mediaEle) {
            // Don't select image with crossorigin attribute if startElem is also image
            if (startElem.tagName.toLowerCase() === 'img' &&
                mediaEle === startElem && !isNOU(startElem.previousSibling)) {
                return null;
            }
            return mediaEle;
        }
        return null;
    }
    // Handles content insertion when the cursor is placed in an inline context without initial selection.
    static handleCursorInlineContent(nodes, insertedNode, range, nodeSelection, docElement, editNode, fragment) {
        let lastSelectionNode;
        const immediateBlockNode = this.getImmediateBlockNode(range.startContainer, editNode);
        const tempSpan = createElement('span', { className: 'tempSpan' });
        if (this.shouldInsertInAnchor(range)) {
            this.insertInAnchor(range, tempSpan, editNode);
        }
        else if (this.isMentionChip(nodes)) {
            range.startContainer.parentElement.insertAdjacentElement('afterend', tempSpan);
        }
        else if (range.startOffset !== 0 && range.endOffset !== 0 && range.startOffset === range.endOffset
            && !insertedNode.querySelector('a') && range.endOffset === range.startContainer.textContent.length && immediateBlockNode.lastChild.contains(range.startContainer)) {
            let node = range.startContainer;
            let insideInline = false;
            while (node && node !== editNode) {
                if (node.nodeType === Node.ELEMENT_NODE && this.formattingInlineNodes.indexOf(node.nodeName.toLowerCase()) !== -1) {
                    insideInline = true;
                    break;
                }
                node = node.parentNode;
            }
            if (insideInline) {
                const inlineFragment = document.createDocumentFragment();
                let lastChildNode = null;
                while (insertedNode.firstChild) {
                    lastChildNode = insertedNode.firstChild;
                    inlineFragment.appendChild(insertedNode.firstChild);
                }
                // Insert inline content at caret
                range.insertNode(inlineFragment);
                // Restore caret using NodeSelection
                if (lastChildNode) {
                    lastSelectionNode = lastChildNode;
                }
                return lastSelectionNode;
            }
            else {
                immediateBlockNode.appendChild(tempSpan);
            }
        }
        else {
            range.insertNode(tempSpan);
        }
        while (insertedNode.firstChild) {
            lastSelectionNode = insertedNode.firstChild;
            fragment.appendChild(insertedNode.firstChild);
        }
        return this.insertFragmentOrReplaceNode(tempSpan, fragment, lastSelectionNode, editNode);
    }
    //Determines if content should be inserted within an anchor element based on specified conditions.
    static shouldInsertInAnchor(range) {
        const nearestAnchor = closest(range.startContainer.parentElement, 'a');
        return range.startContainer.nodeType === 3 &&
            !isNOU(nearestAnchor) &&
            !isNOU(closest(nearestAnchor, 'span'));
    }
    // Specifically inserts nodes inside an anchor tag if conditions are met during paste.
    static insertInAnchor(range, tempSpan, editNode) {
        const immediateBlockNode = this.getImmediateBlockNode(range.startContainer, editNode);
        if (immediateBlockNode.querySelectorAll('br').length > 0) {
            detach(immediateBlockNode.querySelector('br'));
        }
        const rangeElement = closest(closest(range.startContainer.parentElement, 'a'), 'span');
        rangeElement.appendChild(tempSpan);
    }
    // Checks if the node includes a mentions chip for handling special paste scenarios.
    static isMentionChip(nodes) {
        return nodes[0] &&
            nodes[0].nodeName === '#text' &&
            nodes[0].nodeValue.includes('\u200B') &&
            !isNOU(nodes[0].parentElement) &&
            !isNOU(nodes[0].parentElement.previousElementSibling) &&
            nodes[0].parentElement.previousElementSibling.classList.contains('e-mention-chip');
    }
    // Inserts a document fragment at a temporary span position or replaces a specific node.
    static insertFragmentOrReplaceNode(tempSpan, fragment, lastSelectionNode, editNode) {
        const matchedElement = this.getClosestMatchingElement(tempSpan.parentNode, fragment, editNode);
        const hasMultipleChildNodes = fragment.firstChild && fragment.firstChild.childNodes.length > 1;
        if (fragment.childNodes.length === 1 && fragment.firstChild && !hasMultipleChildNodes && matchedElement) {
            return this.replaceWithMatchedContent(tempSpan, fragment, lastSelectionNode);
        }
        else {
            tempSpan.parentNode.replaceChild(fragment, tempSpan);
            return lastSelectionNode;
        }
    }
    // Replaces the temporary node with matched content, adjusting text nodes if required.
    static replaceWithMatchedContent(tempSpan, fragment, lastSelectionNode) {
        const wrapperDiv = document.createElement('div');
        wrapperDiv.innerHTML = fragment.firstChild.innerHTML || '';
        const result = lastSelectionNode;
        if (!isNOU(wrapperDiv.firstChild)) {
            this.addCursorMarker(wrapperDiv, true);
            tempSpan.outerHTML = wrapperDiv.innerHTML;
        }
        wrapperDiv.remove();
        return result;
    }
    // Manages block node insertion during paste operations to align with document structure.
    static handleBlockNodeContent(nodes, insertedNode, range, nodeCutter, editNode, enterAction, isCollapsed) {
        const parentElem = this.findParentPreElement(range, editNode);
        if (!isNOU(insertedNode) && !isNOU(parentElem) && parentElem.nodeName === 'PRE') {
            range.insertNode(insertedNode);
            return insertedNode.lastChild;
        }
        else {
            return this.processBlockContent(nodes, insertedNode, range, nodeCutter, editNode, enterAction, isCollapsed);
        }
    }
    // Finds the nearest parent PRE element starting from the current range container.
    static findParentPreElement(range, editNode) {
        let parentElem = range.startContainer;
        while (!isNOU(parentElem) && parentElem.nodeName !== 'PRE' && parentElem !== editNode) {
            parentElem = parentElem.parentElement;
        }
        return parentElem;
    }
    /* Processes the inserted nodes, preserving initial nodes until first block element,
    then wrapping inline nodes between blocks with appropriate container elements */
    static processInlineNodesBetweenBlocks(insertedNode, enterAction) {
        const fragment = document.createDocumentFragment();
        let foundFirstBlock = false;
        let currentGroup = null;
        let lastNode = null;
        const tempElement = createElement('div', { id: 'pasteContent_rte' });
        while (insertedNode.firstChild) {
            const currentNode = insertedNode.firstChild;
            // Skip empty text nodes
            if (currentNode.nodeName === '#text' && currentNode.textContent.trim() === '') {
                detach(currentNode);
                continue;
            }
            // Keep track of last processed node
            lastNode = currentNode;
            // Check if this is a block element
            const isBlockNode = this.isBlockElement(currentNode);
            if (!foundFirstBlock) {
                // Before first block is encountered, preserve original structure
                if (isBlockNode) {
                    // First block found, change mode
                    foundFirstBlock = true;
                    fragment.appendChild(currentNode);
                }
                else {
                    tempElement.appendChild(currentNode);
                    fragment.appendChild(tempElement);
                }
            }
            else {
                // After first block, apply wrapping logic
                if (isBlockNode) {
                    // Add block elements directly, close any open group
                    currentGroup = null;
                    fragment.appendChild(currentNode);
                }
                else {
                    // Wrap inline/text nodes
                    if (!currentGroup) {
                        // Create new wrapper if needed
                        currentGroup = enterAction === 'DIV' ?
                            createElement('div') : createElement('p');
                        fragment.appendChild(currentGroup);
                    }
                    // Add to current group
                    currentGroup.appendChild(currentNode);
                }
            }
        }
        return { fragment, lastNode };
    }
    // Checks whether the given node is a block element.
    static isBlockElement(node) {
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return false;
        }
        const blockTags = CONSTANT.BLOCK_TAGS;
        const nodeName = node.nodeName.toLowerCase();
        for (let i = 0; i < blockTags.length; i++) {
            if (blockTags[i] === nodeName) {
                return true;
            }
        }
        return false;
    }
    // Processes block elements during insertion, wrapping and positioning elements as needed.
    static processBlockContent(nodes, insertedNode, range, nodeCutter, editNode, enterAction, isCollapsed) {
        let lastSelectionNode = null;
        const insertedFragment = this.processInlineNodesBetweenBlocks(insertedNode, enterAction);
        let currentNode = range.startContainer;
        let insideInline = false;
        let inlineCandidate = false;
        let foundListOrTable = false;
        while (currentNode && currentNode !== editNode) {
            const tagName = currentNode.nodeName.toLowerCase();
            if (tagName === 'li' || tagName === 'table' || tagName === 'tbody' ||
                tagName === 'tr' || tagName === 'td' || tagName === 'th') {
                foundListOrTable = true;
                break;
            }
            if (this.formattingInlineNodes.indexOf(tagName) !== -1) {
                inlineCandidate = true;
            }
            currentNode = currentNode.parentNode;
        }
        insideInline = inlineCandidate && !foundListOrTable;
        if (insideInline) {
            const inlineWrapper = insertedFragment.fragment.querySelector('#pasteContent_rte');
            if (inlineWrapper && inlineWrapper.firstChild) {
                const inlineFragment = document.createDocumentFragment();
                let lastInlineChild = null;
                while (inlineWrapper.firstChild) {
                    lastInlineChild = inlineWrapper.firstChild;
                    inlineFragment.appendChild(inlineWrapper.firstChild);
                }
                // Insert inline content at caret
                range.insertNode(inlineFragment);
                if (lastInlineChild && lastInlineChild.parentNode) {
                    range.setStartAfter(lastInlineChild);
                    range.collapse(true);
                }
                const currentDocument = editNode.ownerDocument;
                const selection = currentDocument.getSelection();
                if (selection) {
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
                inlineWrapper.remove();
                lastSelectionNode = lastInlineChild;
            }
        }
        // Insert a temporary node and get ready to process content
        lastSelectionNode = this.insertTempNode(range, insertedFragment.fragment, nodes, nodeCutter, editNode);
        // Delete existing contents if needed
        if (!this.contentsDeleted) {
            this.cleanupBeforeBlockInsertion(range, editNode, isCollapsed);
        }
        const inlineNodeWrapper = editNode.querySelector('#pasteContent_rte');
        if (!isNOU(inlineNodeWrapper)) {
            this.processFirstInlineNodeSet(inlineNodeWrapper, enterAction);
        }
        return lastSelectionNode;
    }
    // Performs necessary cleanup actions prior to block element insertion, like removing empties.
    static cleanupBeforeBlockInsertion(range, editNode, isCollapsed) {
        if (!isCollapsed &&
            range.startContainer.parentElement.textContent.length === 0 &&
            range.startContainer.nodeName === 'BR' &&
            range.startContainer.parentElement.nodeName === 'P') {
            editNode.removeChild(range.startContainer.parentElement);
        }
        range.deleteContents();
        this.removeEmptyElements(editNode);
    }
    // Processes and adjusts the first set of inline nodes before any block.
    static processFirstInlineNodeSet(insertedNode, enterAction) {
        let lastSelectionNode;
        while (insertedNode.firstChild) {
            lastSelectionNode = insertedNode.firstChild;
            if (this.isInlineElement(lastSelectionNode)) {
                lastSelectionNode = this.handleFirstBlockChild(insertedNode, enterAction);
            }
            else {
                break; // Prevent infinite loop
            }
        }
        detach(insertedNode);
        return lastSelectionNode;
    }
    // Moves the first set of inline nodes to the previous block element a block.
    static handleFirstBlockChild(insertedNode, enterAction) {
        const firstChild = insertedNode.firstChild;
        // Ensure there's a previous element sibling
        if (isNOU(insertedNode.previousElementSibling)) {
            const firstParaElm = enterAction === 'DIV' ? createElement('div') : createElement('p');
            insertedNode.parentElement.insertBefore(firstParaElm, insertedNode);
        }
        // Insert based on previous sibling type
        if (insertedNode.previousElementSibling.nodeName === 'BR') {
            insertedNode.parentElement.insertBefore(insertedNode.firstChild, insertedNode);
        }
        else {
            insertedNode.previousElementSibling.appendChild(insertedNode.firstChild);
        }
        return firstChild;
    }
    // Checks if a given node is an inline node.
    static isInlineElement(node) {
        return node.nodeName === '#text' ||
            (this.inlineNode.indexOf(node.nodeName.toLowerCase()) >= 0);
    }
    // Handles special cases in node structures that require custom processing post-insertion.
    static processSpecialNodes(lastSelectionNode, insertedNode, enterAction) {
        if (!lastSelectionNode) {
            return null;
        }
        // Handle Google Sheets HTML
        if (lastSelectionNode instanceof Element && lastSelectionNode.nodeName === 'GOOGLE-SHEETS-HTML-ORIGIN') {
            return this.processGoogleSheetsTable(lastSelectionNode);
        }
        // Handle table nodes to insert paragraphs after tables if there is no content after table.
        if (lastSelectionNode.nodeName === 'TABLE') {
            return this.addParagraphAfterTable(lastSelectionNode, enterAction);
        }
        return lastSelectionNode;
    }
    // Processes table nodes that originate from Google Sheets for alignment adjustments.
    static processGoogleSheetsTable(node) {
        const tableEle = node.querySelector('table');
        const colGroup = tableEle.querySelector('colgroup');
        if (colGroup) {
            for (let i = 0; i < tableEle.rows.length; i++) {
                for (let k = 0; k < tableEle.rows[i].cells.length; k++) {
                    const col = colGroup.querySelectorAll('col')[k];
                    if (col && col.hasAttribute('width')) {
                        const width = col.getAttribute('width');
                        tableEle.rows[i].cells[k].style.width = width + 'px';
                    }
                }
            }
        }
        return node;
    }
    // Inserts a paragraph after a table node to ensure continuity in the document.
    static addParagraphAfterTable(tableNode, enterAction) {
        const pTag = createElement(enterAction === 'DIV' ? 'div' : 'p');
        pTag.appendChild(createElement('br'));
        tableNode.parentElement.insertBefore(pTag, tableNode.nextSibling);
        return pTag;
    }
    // Positions the editor cursor appropriately after completing a paste operation.
    static positionCursorAfterPaste(lastSelectionNode, insertedNode, nodeSelection, docElement, editNode, enterAction) {
        if (!lastSelectionNode) {
            return;
        }
        if (lastSelectionNode.nodeName === '#text') {
            this.placeCursorEnd(lastSelectionNode, insertedNode, nodeSelection, docElement, editNode);
        }
        else if (lastSelectionNode.nodeName === 'HR') {
            this.handleHRElementCursor(lastSelectionNode, nodeSelection, docElement, enterAction);
        }
        else if (editNode.contains(lastSelectionNode) && isNOU(editNode.querySelector('.paste-cursor'))) {
            this.cursorPos(lastSelectionNode, insertedNode, nodeSelection, docElement, editNode);
        }
        else {
            this.handleListElementCursor(insertedNode, editNode, nodeSelection, docElement);
        }
    }
    static handleListElementCursor(insertedNode, editNode, nodeSelection, docElement) {
        const cursorElm = editNode.querySelector('.paste-cursor');
        const nodeList = editNode.querySelectorAll('.pasteContent_RTE');
        const brElement = editNode.querySelector('br.rte-temp-br');
        if (!isNOU(cursorElm)) {
            nodeSelection.setCursorPoint(docElement, cursorElm, 0);
            cursorElm.remove();
        }
        else if (nodeList.length > 0) {
            const lastElement = nodeList[nodeList.length - 1];
            this.cursorPos(lastElement, insertedNode, nodeSelection, docElement, editNode);
        }
        else if (!isNOU(brElement)) {
            nodeSelection.setCursorPoint(docElement, brElement, 0);
        }
    }
    // Handles cursor placement after inserting horizontal rule elements in the document.
    static handleHRElementCursor(lastSelectionNode, nodeSelection, docElement, enterAction) {
        let nextSiblingNode = lastSelectionNode.nextSibling;
        while (nextSiblingNode && nextSiblingNode.nodeName === '#text' && nextSiblingNode.textContent.trim() === '') {
            nextSiblingNode = nextSiblingNode.nextSibling;
        }
        const siblingTag = createElement(enterAction === 'DIV' ? 'div' : 'p');
        siblingTag.appendChild(createElement('br'));
        let parentNode = lastSelectionNode.parentNode;
        if (nextSiblingNode && (nextSiblingNode.nodeName === 'HR' || nextSiblingNode.nodeName === 'TABLE')) {
            parentNode.insertBefore(siblingTag, nextSiblingNode);
            lastSelectionNode = siblingTag;
        }
        else if (parentNode && parentNode.nodeName === 'LI') {
            let currentNode = lastSelectionNode.nextSibling;
            // Traverse through siblings of the <hr> to find a valid non-empty node
            while (currentNode && (currentNode.nodeType === Node.TEXT_NODE && currentNode.textContent.trim() === '')) {
                currentNode = currentNode.nextSibling;
            }
            // If no valid sibling is found, move up to the parent and check for the parent's siblings
            while (!currentNode && parentNode) {
                if (parentNode && (parentNode.nodeName === 'OL' || parentNode.nodeName === 'UL' || parentNode.nodeName === 'LI' || parentNode.nodeName === 'BLOCKQUOTE')) {
                    currentNode = parentNode.nextSibling;
                    // Traverse parent's siblings
                    while (currentNode && (currentNode.nodeType === Node.TEXT_NODE && currentNode.textContent.trim() === '')) {
                        currentNode = currentNode.nextSibling;
                    }
                }
                parentNode = parentNode.parentNode;
            }
            if (isNOU(currentNode)) {
                lastSelectionNode.parentNode.appendChild(siblingTag);
            }
            lastSelectionNode = currentNode ? currentNode : siblingTag;
        }
        else if (nextSiblingNode) {
            const firstChildElement = nextSiblingNode.firstChild;
            if (firstChildElement && firstChildElement.nodeName !== '#text' && firstChildElement.hasAttribute('class') && firstChildElement.classList.contains('rte-temp-br')) {
                nextSiblingNode.firstChild.removeAttribute('class');
            }
            lastSelectionNode = nextSiblingNode;
        }
        else {
            parentNode.appendChild(siblingTag);
            parentNode.insertBefore(lastSelectionNode, siblingTag);
            lastSelectionNode = siblingTag;
        }
        nodeSelection.setSelectionText(docElement, lastSelectionNode, lastSelectionNode, 0, 0);
        return lastSelectionNode;
    }
    // Compares two elements to ensure they are equivalent in terms of tag and relevant attributes.
    static compareParentElements(el1, el2) {
        if (!el1 || !el2) {
            return false;
        }
        if (el1.tagName !== el2.tagName) {
            return false;
        }
        return this.getFilteredAttributes(el1) === this.getFilteredAttributes(el2);
    }
    // Retrieves attributes of an element, filtering out the non-relevant ones for comparison.
    static getFilteredAttributes(element) {
        return Array.from(element.attributes)
            .map((attr) => {
            if (attr.name === 'class') {
                const filteredClass = attr.value.split(' ')
                    .filter((cls) => cls !== 'pasteContent_RTE')
                    .join(' ');
                return filteredClass ? `class='${filteredClass}'` : '';
            }
            return `${attr.name}='${attr.value}'`;
        })
            .filter((attr) => attr.length > 0)
            .sort()
            .join(' ');
    }
    // Identifies the closest matching element in the document fragment from the current node.
    static getClosestMatchingElement(startNode, fragment, editNode) {
        let currentNode = startNode;
        while (currentNode && !currentNode.contains(editNode)) {
            const matchingPastedNode = this.findMatchingChild(fragment, currentNode);
            if (matchingPastedNode) {
                return currentNode;
            }
            currentNode = currentNode.parentElement;
        }
        return null;
    }
    // Finds a child within a parent container that matches the target node by structural properties.
    static findMatchingChild(fragment, targetNode) {
        for (const node of Array.from(fragment.children)) {
            if (this.compareParentElements(node, targetNode)) {
                return node;
            }
            const deeperMatch = this.findMatchingChild(node, targetNode);
            if (deeperMatch) {
                return deeperMatch;
            }
        }
        return null;
    }
    // Executes cleanup operations on lists to ensure consistency after paste operation.
    static listCleanUp(nodeSelection, docElement) {
        const range = nodeSelection.getRange(docElement);
        const startContainer = range.startContainer;
        const startOffset = range.startOffset;
        let hasListCleanUp = false;
        let hasListContainerCleanUp = false;
        const copiedLists = docElement.querySelectorAll('.e-rte-copy-list');
        for (let i = 0; i < copiedLists.length; i++) {
            const listItem = copiedLists[i];
            hasListCleanUp = this.cleanUpListItems(listItem);
            hasListContainerCleanUp = this.cleanUpListContainer(listItem);
            listItem.classList.remove('e-rte-copy-list');
            if (listItem.getAttribute('class').length === 0) {
                listItem.removeAttribute('class');
            }
        }
        if (hasListCleanUp || hasListContainerCleanUp) {
            range.setStart(startContainer, startOffset);
            range.setEnd(startContainer, startOffset);
        }
    }
    // Cleans up list items to restore structural integrity and resolve any post-paste issues.
    static cleanUpListItems(parentContainer) {
        let hasListCleanUp = false;
        let listItems;
        if (!isNOU(parentContainer.closest('ol, ul'))) {
            listItems = parentContainer.closest('ol, ul').querySelectorAll('li');
        }
        if (isNOU(listItems) || listItems.length === 0) {
            return false;
        }
        let nearestListItem = null;
        listItems.forEach((listItem) => {
            const parentElement = listItem.parentElement;
            if (!isNOU(parentElement) && parentElement.nodeName !== 'OL' && parentElement.nodeName !== 'UL') {
                if (isNOU(nearestListItem)) {
                    nearestListItem = parentElement.closest('li');
                }
                if (!isNOU(nearestListItem)) {
                    const nextSibling = listItem.nextSibling;
                    if (!isNOU(nextSibling) && nextSibling.nodeName !== 'LI') {
                        const startIndex = Array.prototype.indexOf.call(parentElement.childNodes, nextSibling);
                        const clonedParent = parentElement.cloneNode(false);
                        const totalChildren = parentElement.childNodes.length;
                        for (let i = startIndex; i < totalChildren; i++) {
                            clonedParent.appendChild(parentElement.childNodes[startIndex]);
                        }
                        if (clonedParent.childNodes.length > 0) {
                            const newListItem = document.createElement('li');
                            newListItem.appendChild(clonedParent);
                            nearestListItem.insertAdjacentElement('afterend', newListItem);
                        }
                        else {
                            clonedParent.remove();
                        }
                    }
                    const closestList = parentElement.closest('ol, ul');
                    nearestListItem.insertAdjacentElement('afterend', listItem);
                    nearestListItem = nearestListItem.nextSibling;
                    if (!isNOU(closestList)) {
                        this.removeEmptyElements(closestList);
                    }
                    hasListCleanUp = true;
                }
            }
        });
        const cleanUpFlattenListContainer = this.cleanUpFlattenListContainer(parentContainer);
        hasListCleanUp = cleanUpFlattenListContainer ? cleanUpFlattenListContainer : hasListCleanUp;
        return hasListCleanUp;
    }
    // Manages cleanup processes for deeply nested list elements as necessary.
    static cleanUpFlattenListContainer(parentContainer) {
        let hasListCleanUp = false;
        let listItems;
        if (!isNOU(parentContainer.closest('ol, ul'))) {
            listItems = parentContainer.closest('ol, ul').querySelectorAll('li');
        }
        if (isNOU(listItems) || listItems.length === 0) {
            return false;
        }
        listItems.forEach((listItem) => {
            if (!isNOU(listItem.firstChild) && (listItem.firstChild.nodeName === 'OL' || listItem.firstChild.nodeName === 'UL')) {
                listItem.style.listStyleType = 'none';
            }
            const nestedLi = Array.from(listItem.children).find((child) => child.tagName === 'LI' && (child.parentElement && child.parentElement.tagName !== 'OL' && child.parentElement.tagName !== 'UL'));
            if (!isNOU(nestedLi) && !isNOU(listItem.parentNode)) {
                listItem.parentNode.replaceChild(nestedLi, listItem);
                if (isNOU(nestedLi.textContent) || nestedLi.textContent.trim() === '') {
                    nestedLi.remove();
                }
                hasListCleanUp = true;
            }
        });
        return hasListCleanUp;
    }
    // Resolves inconsistencies within list containers, ensuring no stray elements are left.
    static cleanUpListContainer(parentList) {
        let hasListContainerCleanUp = false;
        let nonLiElementCollection = [];
        const replacements = [];
        if (!isNOU(parentList)) {
            for (let i = 0; i < parentList.childNodes.length; i++) {
                const childNode = parentList.childNodes[i];
                const isListNode = ['UL', 'OL'].indexOf(childNode.nodeName) !== -1;
                const hasEmptyTextSibling = isNOU(childNode.previousSibling) || (childNode.previousSibling &&
                    childNode.previousSibling.nodeType === Node.TEXT_NODE &&
                    childNode.previousSibling.textContent.trim() === '');
                const prevElement = childNode.previousElementSibling;
                const isPrevLi = prevElement && prevElement.nodeName.toUpperCase() === 'LI';
                if (isListNode && (hasEmptyTextSibling || isPrevLi) && prevElement) {
                    prevElement.appendChild(childNode);
                    this.cleanUpListContainer(childNode);
                    i--;
                    hasListContainerCleanUp = true;
                }
                else if (childNode.nodeName.toLocaleUpperCase() !== 'LI') {
                    nonLiElementCollection.push(childNode);
                }
                if ((childNode.nodeName.toLocaleUpperCase() === 'LI' || parentList.lastChild === childNode) && nonLiElementCollection.length > 0) {
                    replacements.push({ elements: [...nonLiElementCollection] });
                    nonLiElementCollection = [];
                }
            }
            replacements.forEach(({ elements }) => {
                const newListItem = document.createElement('li');
                elements[0].parentNode.replaceChild(newListItem, elements[0]);
                elements.forEach((child) => newListItem.appendChild(child));
                if (newListItem.textContent && newListItem.textContent.trim() === '' && !newListItem.querySelector('img')) {
                    parentList.removeChild(newListItem);
                }
                if (newListItem.firstElementChild && (newListItem.firstElementChild.nodeName === 'OL' || newListItem.firstElementChild.nodeName === 'UL')) {
                    newListItem.style.listStyleType = 'none';
                }
                hasListContainerCleanUp = true;
            });
        }
        return hasListContainerCleanUp;
    }
    // Moves the cursor to the end of the content node, ensuring proper placement.
    static placeCursorEnd(lastSelectionNode, insertedNode, nodeSelection, docElement, editNode) {
        while (!isNOU(lastSelectionNode) && lastSelectionNode.nodeName !== '#text' && lastSelectionNode.nodeName !== 'IMG' &&
            lastSelectionNode.nodeName !== 'VIDEO' && lastSelectionNode.nodeName !== 'AUDIO' && lastSelectionNode.nodeName !== 'BR' && lastSelectionNode.nodeName !== 'HR') {
            if (!isNOU(lastSelectionNode.lastChild) && (lastSelectionNode.lastChild.nodeName === 'P' &&
                lastSelectionNode.lastChild.innerHTML === '')) {
                const lineBreak = createElement('br');
                lastSelectionNode.lastChild.appendChild(lineBreak);
            }
            lastSelectionNode = lastSelectionNode.lastChild;
        }
        lastSelectionNode = isNOU(lastSelectionNode) ? insertedNode : lastSelectionNode;
        if (lastSelectionNode.nodeName === 'IMG' || lastSelectionNode.nodeName === 'VIDEO' || lastSelectionNode.nodeName === 'AUDIO') {
            this.mediaFocus(lastSelectionNode, nodeSelection, docElement);
        }
        else {
            nodeSelection.setSelectionText(docElement, lastSelectionNode, lastSelectionNode, lastSelectionNode.textContent.length, lastSelectionNode.textContent.length);
        }
        this.removeEmptyElements(editNode);
    }
    // Retrieves a collection of nodes from the current selection range for insertion purposes.
    static getNodeCollection(range, nodeSelection, insertedNode) {
        let nodes = [];
        if (range.startOffset === range.endOffset && range.startContainer === range.endContainer &&
            range.startContainer.nodeName !== 'BR' && range.startContainer.childNodes.length > 0 &&
            (range.startContainer.nodeName === 'TD' || (range.startContainer.nodeType !== 3 &&
                insertedNode.classList && insertedNode.classList.contains('pasteContent')))) {
            nodes.push(range.startContainer.childNodes[range.endOffset]);
        }
        else {
            nodes = nodeSelection.getInsertNodeCollection(range);
        }
        return nodes;
    }
    // Inserts a temporary node at the appropriate position based on range state and node types.
    static insertTempNode(range, insertedNode, nodes, nodeCutter, editNode) {
        let lastSelectionNode = insertedNode.lastChild;
        // Handle insertion after a TABLE when selection is at editor root
        if (this.shouldInsertAfterTable(range, editNode) && range.collapsed) {
            this.insertNodeAfterTable(range.startContainer, insertedNode, range.endOffset - 1);
            return lastSelectionNode;
        }
        // Handle insertion before a TABLE when selection is at editor root
        if (this.shouldInsertBeforeTable(range, editNode) && range.collapsed) {
            this.insertNodeBeforeTable(range.startContainer, insertedNode, range.startOffset);
            return lastSelectionNode;
        }
        // Handle insertion at the end of editor when table is at cursor
        if (this.shouldAppendAfterTableAtCursor(range, editNode)) {
            range.startContainer.appendChild(insertedNode);
            return lastSelectionNode;
        }
        // Standard insertion cases
        lastSelectionNode = this.handleStandardNodeInsertion(range, insertedNode, nodes, nodeCutter, editNode);
        return lastSelectionNode;
    }
    // Checks if we should insert after a table element at editor root.
    static shouldInsertAfterTable(range, editNode) {
        const startContainer = range.startContainer.nodeType === Node.TEXT_NODE
            ? range.startContainer.parentNode
            : range.startContainer;
        return (startContainer === editNode || startContainer.closest('table')) &&
            !isNOU(startContainer.childNodes[range.endOffset - 1]) &&
            startContainer.childNodes[range.endOffset - 1].nodeName === 'TABLE';
    }
    // Inserts node after a table element.
    static insertNodeAfterTable(container, insertedNode, index) {
        if (isNOU(container.childNodes[index].nextSibling)) {
            container.appendChild(insertedNode);
        }
        else {
            container.insertBefore(insertedNode, container.childNodes[index].nextSibling);
        }
    }
    static shouldInsertBeforeTable(range, editNode) {
        return range.startContainer === editNode &&
            !isNOU(range.startContainer.childNodes[range.startOffset]) &&
            range.startContainer.childNodes[range.startOffset].nodeName === 'TABLE';
    }
    static insertNodeBeforeTable(container, insertedNode, index) {
        if (index >= 0 && index < container.childNodes.length) {
            container.insertBefore(insertedNode, container.childNodes[index]);
        }
        else {
            container.appendChild(insertedNode);
        }
    }
    // Checks if we should append after a table at cursor position
    static shouldAppendAfterTableAtCursor(range, editNode) {
        return range.startContainer === editNode &&
            !isNOU(range.startContainer.childNodes[range.endOffset]) &&
            range.startContainer.childNodes[range.endOffset].nodeName === 'TABLE';
    }
    // Handles standard node insertion cases.
    static handleStandardNodeInsertion(range, insertedNode, nodes, nodeCutter, editNode) {
        // Find appropriate block node for insertion
        let blockNode = this.findBlockNodeForInsertion(range, nodes, editNode);
        // Handle list-specific processing for inserted nodes
        this.processListItemsInNode(blockNode, insertedNode, editNode);
        const lastSelectionNode = insertedNode.lastChild;
        // Handle table cell insertion
        if (this.isTableCellNode(blockNode)) {
            this.insertInTableCell(range, insertedNode, blockNode, nodeCutter, editNode);
            return lastSelectionNode;
        }
        const emptyBlockEle = this.isHorizontalRuleInEmptyBlock(lastSelectionNode, range);
        // When inserting HR and selection is in a P/DIV with only BR
        if (!isNOU(emptyBlockEle)) {
            const containerParent = emptyBlockEle.parentNode;
            containerParent.replaceChild(insertedNode, emptyBlockEle);
            return lastSelectionNode;
        }
        // Handle media elements
        if (this.isMediaElement(blockNode)) {
            blockNode = range.startContainer;
        }
        // Handle other insertion cases
        this.handleRegularInsertion(range, insertedNode, blockNode, nodeCutter, editNode);
        return lastSelectionNode;
    }
    // Finds appropriate block node for insertion.
    static findBlockNodeForInsertion(range, nodes, editNode) {
        // Check if the entire table is selected — if so, use TABLE as the block node directly.
        const entireTableNode = this.getEntireTableSelectionNode(range, editNode);
        if (!isNOU(entireTableNode)) {
            return entireTableNode;
        }
        let blockNode = this.getImmediateBlockNode(nodes[nodes.length - 1], editNode);
        // Fallback to range end container if no block node found
        if ((isNOU(blockNode) || isNOU(blockNode.parentElement)) && range.endContainer.nodeType !== 3) {
            blockNode = range.endContainer;
            range.setEnd(blockNode, range.endContainer.textContent.length);
        }
        // Special handling for body/div block nodes
        if (blockNode && (blockNode.nodeName === 'BODY' ||
            (blockNode.nodeName === 'DIV' && range.startContainer === range.endContainer && range.startContainer.nodeType === 1))) {
            blockNode = range.startContainer;
        }
        return blockNode;
    }
    // Checks whether the entire table is covered by the selection range.
    // Returns the TABLE node when the end is at the last cell's end and start is at first cell or outside.
    static getEntireTableSelectionNode(range, editNode) {
        const endCell = (range.endContainer.nodeType === Node.TEXT_NODE
            ? range.endContainer.parentElement : range.endContainer).closest('td, th');
        if (isNOU(endCell)) {
            return null;
        }
        const endTable = endCell.closest('table');
        if (isNOU(endTable) || !editNode.contains(endTable)) {
            return null;
        }
        const allCells = endTable.querySelectorAll('td, th');
        if (allCells.length === 0 || endCell !== allCells[allCells.length - 1]) {
            return null;
        }
        if (!this.isRangeEndAtLastCellEnd(range, endCell)) {
            return null;
        }
        const startCell = (range.startContainer.nodeType === Node.TEXT_NODE
            ? range.startContainer.parentElement : range.startContainer).closest('td, th');
        if (!isNOU(startCell)) {
            const startTable = startCell.closest('table');
            if (startTable !== endTable || startCell !== allCells[0]) {
                return null;
            }
        }
        endTable.classList.add('e-entire-table-selected');
        return endTable;
    }
    // Checks if the range's end position is at the absolute end of the last cell's content
    static isRangeEndAtLastCellEnd(range, lastCell) {
        const { endContainer, endOffset } = range;
        if (endContainer.nodeType === Node.TEXT_NODE) {
            return endOffset === endContainer.length && !this.hasContentAfterNode(endContainer, lastCell);
        }
        return endOffset === endContainer.childNodes.length && this.isContainerAtOrAfterLastCell(endContainer, lastCell);
    }
    // Checks if there is any content after the given node within the boundary cell
    static hasContentAfterNode(node, boundaryCell) {
        let nextNode = node.nextSibling;
        while (nextNode) {
            if (nextNode.nodeType === Node.ELEMENT_NODE ||
                (nextNode.nodeType === Node.TEXT_NODE && nextNode.textContent !== '')) {
                return true;
            }
            nextNode = nextNode.nextSibling;
        }
        let parent = node.parentNode;
        while (parent && parent !== boundaryCell) {
            if (parent.nextSibling) {
                return true;
            }
            parent = parent.parentNode;
        }
        return false;
    }
    // Checks if the container is at or encompasses all remaining content in the last cell
    static isContainerAtOrAfterLastCell(container, lastCell) {
        return container === lastCell || (lastCell.contains(container) && !this.hasContentAfterNode(container, lastCell));
    }
    // Processes list items in a node being inserted inside a list context.
    static processListItemsInNode(blockNode, insertedNode, editNode) {
        this.wrapUnstructedLiWithUl(insertedNode);
        // Only process if we're in a list item and inserting a list
        if (!this.shouldProcessListItems(blockNode, insertedNode, editNode)) {
            return;
        }
        let liNode;
        const insertedNodeAsHtml = insertedNode;
        // Extract LI elements from the list and normalize their styles
        while (!isNOU(insertedNodeAsHtml.firstElementChild) &&
            insertedNodeAsHtml.firstElementChild.lastElementChild &&
            insertedNodeAsHtml.firstElementChild.lastElementChild.tagName === 'LI') {
            liNode = insertedNodeAsHtml.firstElementChild.lastElementChild;
            this.removeChecklistStyle(blockNode, liNode);
            this.removeListItemMargins(liNode);
            insertedNodeAsHtml.firstElementChild.insertAdjacentElement('afterend', liNode);
        }
    }
    /*
    * Wraps loose `<li>` elements within a `<ul>` container to ensure proper list structure.
    */
    static wrapUnstructedLiWithUl(insertedNode) {
        const orphanLIs = Array.from(insertedNode.querySelectorAll('li')).filter((li) => {
            return ['UL', 'OL'].indexOf(li.parentNode.nodeName) === -1;
        });
        let ul = null;
        let siblingFlag;
        for (let i = 0; i < orphanLIs.length; i++) {
            const currentLi = orphanLIs[i];
            const isSibling = orphanLIs[i + 1] && currentLi.nextSibling === orphanLIs[i + 1];
            if (isNOU(ul) || !siblingFlag) {
                ul = createElement('ul');
                if (isNOU(currentLi.parentNode)) {
                    insertedNode.insertBefore(ul, currentLi);
                }
                else {
                    currentLi.parentNode.insertBefore(ul, currentLi);
                }
            }
            ul.appendChild(currentLi);
            siblingFlag = isSibling;
        }
    }
    /*
    * Removes checklist-specific inline styles from a pasted list item (`<li>`).
    */
    static removeChecklistStyle(blockNode, liNode) {
        if (blockNode.nodeName === 'LI' && blockNode.parentElement &&
            (blockNode.parentElement.nodeName === 'UL' || blockNode.parentElement.nodeName === 'OL')) {
            liNode.style.removeProperty('list-style');
            liNode.style.removeProperty('position');
            if (liNode.getAttribute('style') === '') {
                liNode.removeAttribute('style');
            }
        }
    }
    // Checks if we should process list items in the node.
    static shouldProcessListItems(blockNode, insertedNode, editNode) {
        return blockNode &&
            blockNode.nodeName !== '#text' &&
            blockNode.closest('LI') &&
            editNode.contains(blockNode.closest('LI')) &&
            blockNode.nodeName !== 'TD' &&
            blockNode.nodeName !== 'TH' &&
            blockNode.nodeName !== 'TR' &&
            insertedNode &&
            insertedNode.firstElementChild &&
            (insertedNode.firstElementChild.tagName === 'OL' ||
                insertedNode.firstElementChild.tagName === 'UL');
    }
    // Removes margin properties from a list item
    static removeListItemMargins(liNode) {
        liNode.style.removeProperty('margin-left');
        liNode.style.removeProperty('margin-top');
        liNode.style.removeProperty('margin-bottom');
        if (liNode.getAttribute('style') === '') {
            liNode.removeAttribute('style');
        }
    }
    // Checks if the node is a table cell
    static isTableCellNode(blockNode) {
        if (!blockNode) {
            return false;
        }
        const nodeName = blockNode.nodeName;
        return nodeName === 'TD' || nodeName === 'TH' || nodeName === 'TR' || nodeName === 'TABLE';
    }
    // Handles insertion in a table cell.
    static insertInTableCell(range, insertedNode, blockNode, nodeCutter, editNode) {
        let parentElem = range.startContainer;
        // Check if parentElem is TD or TH and contains only a BR element
        if ((parentElem.nodeName === 'TD' || parentElem.nodeName === 'TH') && parentElem.childNodes.length === 1 && parentElem.firstChild.nodeName === 'BR') {
            // Replace BR with HR
            parentElem.replaceChild(insertedNode, parentElem.firstChild);
            this.contentsDeleted = true;
            return; // Exit the function after directly replacing
        }
        // Find direct child of the table cell
        while (!isNOU(parentElem) && parentElem.parentElement !== blockNode && parentElem !== editNode) {
            parentElem = parentElem.parentElement;
        }
        range.deleteContents();
        let selectedTable = editNode.querySelector('.e-entire-table-selected');
        if (selectedTable) {
            if (parentElem.parentElement === selectedTable) {
                parentElem = null;
            }
            selectedTable.remove();
            selectedTable = null;
        }
        if (parentElem === editNode) {
            parentElem = parentElem.firstChild;
        }
        const splitedElm = nodeCutter.GetSpliceNode(range, parentElem);
        if (splitedElm) {
            splitedElm.parentNode.replaceChild(insertedNode, splitedElm);
        }
        else {
            range.insertNode(insertedNode);
        }
        this.contentsDeleted = true;
    }
    // Handles regular insertion cases.
    static handleRegularInsertion(range, insertedNode, blockNode, nodeCutter, editNode) {
        const nodeSelection = new NodeSelection(editNode);
        const currentNodes = this.getNodeCollection(range, nodeSelection, insertedNode);
        const currentNode = currentNodes[currentNodes.length - 1];
        let splitedElm;
        // Check if the node is an empty special node (BR, HR, or empty text in LI).
        if (this.isEmptySpecialNode(currentNode)) {
            splitedElm = currentNode;
            if (this.handleEmptySpecialNodeInsertion(range, insertedNode, currentNode)) {
                return; // Only return if fully handled.
            }
        }
        // Check if the node is text or BR in a list item with content.
        else if (this.isTextOrBrInListItem(currentNode, blockNode, editNode)) {
            splitedElm = currentNode;
            if (this.handleTextInListItem(range, insertedNode, currentNode, blockNode, nodeCutter, editNode)) {
                return; // Only return if fully handled.
            }
        }
        // Handle regular node insertion.
        else {
            splitedElm = this.getSplitElementForInsertion(range, nodeCutter, blockNode);
        }
        if (!isNOU(splitedElm) && splitedElm === editNode) {
            range.deleteContents();
            this.removeEmptyElements(editNode);
            range.insertNode(insertedNode);
            this.contentsDeleted = true;
        }
        else if (splitedElm && splitedElm.nodeType === Node.ELEMENT_NODE && range.toString() === '' &&
            splitedElm.querySelector('img, video, audio') !== null) {
            splitedElm.parentNode.insertBefore(insertedNode, splitedElm);
        }
        else {
            // Common replacement logic for all paths that don't return early.
            splitedElm.parentNode.replaceChild(insertedNode, splitedElm);
        }
    }
    // Checks if the node is an empty special node (BR, HR or empty text in LI).
    static isEmptySpecialNode(currentNode) {
        return !!currentNode &&
            ((currentNode.nodeName === 'BR' || currentNode.nodeName === 'HR' ||
                (currentNode.nodeName === '#text' &&
                    !isNOU(currentNode.parentElement) &&
                    currentNode.parentElement.nodeName === 'LI')) &&
                (!isNOU(currentNode.parentElement) &&
                    currentNode.parentElement.textContent.trim().length === 0));
    }
    // Handles insertion when the current node is an empty special node.
    static handleEmptySpecialNodeInsertion(range, insertedNode, currentNode) {
        if (currentNode.parentElement.nodeName === 'LI' &&
            !isNOU(currentNode.nextSibling) &&
            currentNode.nextSibling.nodeName === 'BR') {
            detach(currentNode.nextSibling);
        }
        if ((currentNode.parentElement.nodeName === 'LI' || currentNode.parentElement.closest('li')) &&
            currentNode.parentElement.textContent === '') {
            this.removeListfromPaste(range);
            if (currentNode.parentElement.childNodes.length === 1 &&
                currentNode.nodeName === 'BR') {
                detach(currentNode);
            }
            const filteredChildNodes = Array.from(insertedNode.childNodes).filter((child) => {
                return !(child.nodeName === 'LI' || child.nodeName === 'UL' || child.nodeName === 'OL');
            });
            const insertNodes = this.extractChildNodes(insertedNode);
            if (filteredChildNodes.length > 0 && insertNodes.length > 1) {
                this.insertBlockNodesInLI(insertNodes, range);
            }
            else {
                const startContainerParent = range.startContainer.parentElement;
                const nextSibling = startContainerParent.nextElementSibling;
                if (range.startContainer.nodeName === 'DIV' && startContainerParent &&
                    startContainerParent.nodeName === 'LI' && startContainerParent.parentNode) {
                    startContainerParent.parentNode.insertBefore(insertedNode, nextSibling);
                    detach(startContainerParent); // Detach the parent element after inserting
                }
                else {
                    range.insertNode(insertedNode);
                }
            }
            this.contentsDeleted = true;
            return true; // Indicate we've fully handled this case.
        }
        return false; // Not fully handled, proceed to common replacement.
    }
    // Extracts child nodes of a node.
    static extractChildNodes(node) {
        const children = [];
        for (let i = 0; i < node.childNodes.length; i++) {
            children.push(node.childNodes.item(i));
        }
        return children;
    }
    // Inserts a block nodes in separate list items.
    static insertBlockNodesInLI(children, range) {
        children = this.processInsertNodes(children);
        const fragment = document.createDocumentFragment();
        for (const block of children) {
            const newLi = createElement('li');
            newLi.appendChild(block.cloneNode(true));
            fragment.appendChild(newLi);
        }
        this.unwrapInlineWrappers(fragment);
        const startContainerParent = range.startContainer.parentElement;
        const nextSibling = startContainerParent.nextElementSibling;
        if (range.startContainer.nodeName === 'DIV' && startContainerParent &&
            startContainerParent.nodeName === 'LI' && startContainerParent.parentNode) {
            startContainerParent.parentNode.insertBefore(fragment, nextSibling);
            detach(startContainerParent); // Detach the parent element after inserting
        }
        else {
            range.insertNode(fragment);
        }
    }
    // Processes and adjusts the child nodes before any block.
    static processInsertNodes(children) {
        const result = [];
        let inlineGroup = [];
        for (const child of children) {
            const isBlock = child.nodeType === Node.ELEMENT_NODE &&
                CONSTANT.BLOCK_TAGS.indexOf(child.nodeName.toLowerCase()) !== -1;
            if (isBlock) {
                if (inlineGroup.length > 0) {
                    result.push(this.wrapInlineElementsInSpan(inlineGroup));
                    inlineGroup = [];
                }
                result.push(child);
            }
            else {
                inlineGroup.push(child);
            }
        }
        if (inlineGroup.length > 0) {
            result.push(this.wrapInlineElementsInSpan(inlineGroup));
        }
        return result;
    }
    // Wraps inline elements in a span.
    static wrapInlineElementsInSpan(inlineNodes) {
        const wrapper = createElement('span');
        wrapper.className = 'inline-wrapper';
        inlineNodes.forEach((node) => wrapper.appendChild(node));
        return wrapper;
    }
    // Unwraps inline wrappers
    static unwrapInlineWrappers(root) {
        const wrappers = root.querySelectorAll('.inline-wrapper');
        wrappers.forEach((wrapper) => {
            const parent = wrapper.parentNode;
            if (!parent) {
                return;
            }
            while (wrapper.firstChild) {
                parent.insertBefore(wrapper.firstChild, wrapper);
            }
            parent.removeChild(wrapper);
        });
    }
    // Remove empty list items after start LI
    static removeEmptyAfterStartLI(liElement, editNode) {
        this.clearIfCompletelyEmpty(liElement);
        const rootList = this.getRootList(liElement, editNode);
        if (!rootList) {
            return;
        }
        const listItems = rootList.querySelectorAll('li');
        listItems.forEach((item) => {
            if (this.isRemovableEmptyListItem(item, liElement)) {
                detach(item);
            }
        });
    }
    // Clear if completely empty
    static clearIfCompletelyEmpty(liElement) {
        if (liElement.textContent.length === 0 && !liElement.querySelector('audio,video,img,table,br,hr')) {
            liElement.innerHTML = '';
        }
    }
    // Get root list
    static getRootList(li, editNode) {
        let rootList = closest(li, 'ul,ol');
        while (rootList && rootList.parentElement && editNode.contains(rootList.parentElement)) {
            const parentRootList = closest(rootList.parentElement, 'ul,ol');
            if (editNode.contains(parentRootList)) {
                rootList = parentRootList;
            }
            else {
                return rootList;
            }
        }
        return rootList || null;
    }
    // Remove empty list items
    static isRemovableEmptyListItem(item, skipElement) {
        return item !== skipElement &&
            item.textContent.trim() === '' &&
            !item.querySelector('audio,video,img,table,br,hr');
    }
    // Checks if the node is a text or BR node in a list item.
    static isTextOrBrInListItem(currentNode, blockNode, editNode) {
        return currentNode &&
            ((currentNode.nodeName === '#text' || currentNode.nodeName === 'BR' || currentNode.nodeName === 'HR') &&
                !isNOU(currentNode.parentElement) &&
                (currentNode.parentElement.nodeName === 'LI' ||
                    currentNode.parentElement.closest('LI') ||
                    (blockNode === editNode && currentNode.parentElement === blockNode)) &&
                currentNode.parentElement.textContent.trim().length > 0);
    }
    // Handles insertion when the current node is text in a list item.
    static handleTextInListItem(range, insertedNode, currentNode, parentNode, nodeCutter, editNode) {
        if (currentNode.parentElement.nodeName === 'LI' &&
            !isNOU(currentNode.nextSibling) &&
            currentNode.nextSibling.nodeName === 'BR') {
            detach(currentNode.nextSibling);
        }
        const filteredChildNodes = Array.from(insertedNode.childNodes).filter((child) => {
            return !(child.nodeName === 'LI' || child.nodeName === 'UL' || child.nodeName === 'OL');
        });
        const mergeNode = currentNode.parentElement;
        let cloneRange = null;
        const isCollapsed = range.collapsed;
        const parentLi = isCollapsed ? currentNode.parentElement.closest('LI') : null;
        let startLi = null;
        let endLi = null;
        if (!range.collapsed) {
            const startContainer = range.startContainer;
            const startOffset = range.startOffset;
            cloneRange = range.cloneRange();
            startLi = this.findLiFromContainer(cloneRange.startContainer);
            endLi = this.findLiFromContainer(cloneRange.endContainer);
            this.removeListfromPaste(range);
            if (startLi && filteredChildNodes.length > 0) {
                this.removeEmptyAfterStartLI(startLi, editNode);
            }
            range.setStart(startContainer, startOffset);
            range.setEnd(startContainer, startOffset);
        }
        const startContainerparentElement = range.startContainer.parentElement;
        const blockNode = this.getImmediateBlockNode(currentNode, insertedNode);
        if (insertedNode.firstChild.nodeName === 'HR') {
            let parentListItem = null;
            if (startLi) {
                parentListItem = closest(startLi, 'li');
            }
            else {
                parentListItem = closest(parentNode, 'li');
            }
            parentNode = parentListItem ? parentListItem : parentNode;
            this.insertBlockElementInList(range, insertedNode, parentNode, nodeCutter);
        }
        else if (isCollapsed && parentLi && filteredChildNodes.length > 0) {
            this.pasteLI(insertedNode, parentLi, mergeNode, blockNode, range, nodeCutter);
        }
        else if (!isCollapsed && startLi && endLi && filteredChildNodes.length > 0) {
            this.nonCollapsedInsertion(insertedNode, cloneRange, nodeCutter, endLi);
        }
        else if (isCollapsed && ((insertedNode.firstChild.nodeName === 'UL' ||
            insertedNode.firstChild.nodeName === 'OL') && currentNode.parentElement && currentNode.parentElement.nodeName === 'DIV')) {
            // Case 1: If cursor is at the start of the list item
            if (range.startOffset === 0 && range.endOffset === 0 && startContainerparentElement) {
                startContainerparentElement.parentNode.insertBefore(insertedNode, startContainerparentElement);
            }
            // Case 2: If cursor is at the end of the list item
            else if (range.startOffset === range.startContainer.textContent.length && range.endOffset ===
                range.startContainer.textContent.length &&
                startContainerparentElement && startContainerparentElement.parentNode) {
                const nextSibling = startContainerparentElement.nextSibling;
                startContainerparentElement.parentNode.insertBefore(insertedNode, nextSibling);
            }
            else {
                const liElement = startContainerparentElement.closest('li');
                // Use nodeCutter to split the node at the range
                const spliceNode = nodeCutter.GetSpliceNode(range, startContainerparentElement);
                // Create a new <li> to hold the remaining content after the splice
                const newLi = document.createElement('li');
                // Move all nodes after spliceNode into newLi
                const nextNode = spliceNode.nextSibling;
                newLi.appendChild(nextNode);
                // Insert the pasted list items (insertedNode) after the current <li>
                insertedNode.appendChild(newLi);
                liElement.parentNode.insertBefore(insertedNode, liElement.nextSibling);
            }
        }
        else if (!isCollapsed && ((range.startContainer.nodeName === '#text' && startContainerparentElement.nodeName === 'DIV') ||
            (range.startContainer.nodeName === 'DIV' && startContainerparentElement.nodeName === 'LI'))) {
            const startContainer = (range.startContainer.nodeName === '#text' && range.startContainer.textContent.trim().length === 0) ?
                startContainerparentElement : range.startContainer;
            if (startContainer && startContainer.parentElement.nodeName === 'LI') {
                startContainerparentElement.parentNode.insertBefore(insertedNode, startContainerparentElement.nextSibling);
            }
        }
        else {
            range.insertNode(insertedNode);
        }
        this.contentsDeleted = true;
        return true; // Indicate we've fully handled this case.
    }
    // Returns a LI node from any container
    static findLiFromContainer(container) {
        if (container.nodeName === 'LI') {
            return container;
        }
        let parent = container.nodeType === Node.TEXT_NODE ? container.parentNode : container;
        parent = parent.nodeName === 'LI' ? parent : parent.closest('LI');
        return parent;
    }
    //Handles non-collapsed list insertion logic for splitting and merging list items based on selection range.
    static nonCollapsedInsertion(insertedNode, cloneRange, nodeCutter, endSelectionLi) {
        let children = this.extractChildNodes(insertedNode);
        children = this.processInsertNodes(children);
        const startContainer = cloneRange.startContainer;
        const endContainer = cloneRange.endContainer;
        const isEndContainerLi = endContainer.nodeName === 'UL' || endContainer.nodeName === 'OL';
        const parentLi = this.getClosestLi(startContainer);
        const previousLi = this.getPreviousLi(parentLi);
        let endLi = this.getNextLi(parentLi);
        const parentList = parentLi.parentNode;
        if (endLi && parentList === endContainer) {
            if (isEndContainerLi && endSelectionLi.textContent === '') {
                endLi = null;
            }
        }
        if (startContainer === endContainer || (!endLi || (parentLi.contains(endContainer) && !isEndContainerLi)) &&
            !this.isAnotherLiFromEndLi || this.isAnotherLiFromEndLi && parentList !== endContainer && endContainer.nodeName !== 'A') {
            this.handleSingleLiInsertion(parentLi, previousLi, endLi, children, startContainer, cloneRange, nodeCutter, parentList);
        }
        else {
            this.handleMultiLiInsertion(parentLi, children, startContainer, endContainer, parentList);
        }
        this.unwrapInlineWrappers(parentList);
    }
    // Returns the nearest ancestor LI element for a given node
    static getClosestLi(node) {
        let current = node.nodeType === Node.TEXT_NODE ? node.parentNode : node;
        while (current && current.nodeName !== 'LI') {
            current = current.parentNode;
        }
        return current;
    }
    // Returns the previous LI sibling if available
    static getPreviousLi(li) {
        const prev = li.previousSibling;
        return (prev && prev.nodeName === 'LI') ? prev : null;
    }
    // Returns the next LI sibling if available
    static getNextLi(li) {
        const next = li.nextSibling;
        return (next && next.nodeName === 'LI') ? next : null;
    }
    // Appends list items to a fragment and returns the last appended list item
    static appendListItems(fragment, children, startIndex, endIndex) {
        let lastNewLi = null;
        for (let i = startIndex; i < endIndex; i++) {
            const li = document.createElement('li');
            li.appendChild(children[i]);
            fragment.appendChild(li);
            lastNewLi = li;
        }
        return lastNewLi;
    }
    // Handles insertion when start and end container are in different LIs
    static moveSiblingsToLiAndInsert(fromNode, targetLi, fragment, parentLi, parentList) {
        const elementsToMove = [];
        while (fromNode) {
            elementsToMove.push(fromNode);
            fromNode = fromNode.nextSibling;
        }
        for (let i = 0; i < elementsToMove.length; i++) {
            if (parentLi.contains(elementsToMove[i])) {
                parentLi.removeChild(elementsToMove[i]);
            }
        }
        for (let i = 0; i < elementsToMove.length; i++) {
            targetLi.appendChild(elementsToMove[i]);
        }
        if (parentLi.nextSibling) {
            parentList.insertBefore(fragment, parentLi.nextSibling);
        }
        else {
            parentLi.appendChild(fragment);
        }
    }
    // Handles insertion when start and end container are in same LI or no end LI
    static handleSingleLiInsertion(parentLi, previousLi, endLi, children, startContainer, cloneRange, nodeCutter, parentList) {
        const fragment = document.createDocumentFragment();
        this.extractNestedListsIntoNewListItem(parentLi);
        let middleLi = null;
        let lastNode = null;
        let preNode = parentLi.hasChildNodes() &&
            (parentLi.lastChild.nodeType === Node.TEXT_NODE || parentLi.textContent === '')
            ? parentLi : parentLi.lastChild;
        if (startContainer && startContainer.textContent && startContainer.textContent.length > 0) {
            middleLi = nodeCutter.GetSpliceNode(cloneRange, startContainer);
            preNode = middleLi.previousSibling !== previousLi ? middleLi.previousSibling : null;
            lastNode = middleLi.nextSibling !== endLi ? middleLi.nextSibling : null;
        }
        const firstBlock = children[0];
        const isSingleBlock = children.length === 1;
        if (isSingleBlock) {
            if (lastNode) {
                this.addCursorMarker(lastNode);
                this.moveAllChildren(lastNode, firstBlock);
                lastNode.parentNode.removeChild(lastNode);
            }
            else {
                this.addCursorMarker(firstBlock, true);
            }
        }
        if (preNode && preNode !== previousLi && preNode.textContent && preNode.textContent.length > 0) {
            this.moveAllChildren(firstBlock, preNode);
        }
        else if (isSingleBlock && parentLi.textContent === '') {
            parentLi.appendChild(firstBlock);
        }
        else {
            const newLi = createElement('li');
            newLi.appendChild(firstBlock);
            fragment.appendChild(newLi);
        }
        const lastNewLi = this.appendListItems(fragment, children, 1, children.length);
        if (lastNewLi && lastNode) {
            this.addCursorMarker(lastNode);
            if (lastNode.nodeName === 'A') {
                lastNewLi.lastChild.appendChild(lastNode);
            }
            else {
                this.mergeLastNodeContent(lastNode, lastNewLi);
            }
        }
        const shouldInsertAfter = lastNode && (lastNode.nodeName === 'LI' || !lastNode.nextSibling);
        if (shouldInsertAfter) {
            parentList.insertBefore(fragment, parentLi.nextSibling);
            if (lastNode && lastNode.parentNode && lastNode.textContent.length === 0) {
                lastNode.parentNode.removeChild(lastNode);
            }
        }
        else if (lastNewLi) {
            this.moveSiblingsToLiAndInsert(lastNode, lastNewLi, fragment, parentLi, parentList);
        }
        if (middleLi && middleLi.parentNode && middleLi.textContent === '') {
            middleLi.parentNode.removeChild(middleLi);
        }
        if (parentLi && parentLi.parentNode && parentLi.textContent === '') {
            parentLi.parentNode.removeChild(parentLi);
        }
    }
    // Handles insertion when selection spans multiple LIs
    static handleMultiLiInsertion(parentLi, children, startContainer, endContainer, parentList) {
        const fragment = document.createDocumentFragment();
        this.extractNestedListsIntoNewListItem(parentLi);
        const endLi = parentLi.nextSibling;
        if (endLi) {
            this.extractNestedListsIntoNewListItem(endLi);
        }
        startContainer = startContainer.nodeType === Node.TEXT_NODE ? startContainer.parentNode : startContainer;
        if (endContainer.textContent === '' && endContainer.nextSibling) {
            endContainer = endContainer.nextSibling;
        }
        if (!endLi.contains(endContainer) || endContainer.nodeName === 'UL' || endContainer.nodeName === 'OL') {
            endContainer = endLi;
        }
        const firstBlock = children[0];
        const lastBlock = children[children.length - 1];
        if (endContainer.nodeType === Node.TEXT_NODE && children.length > 1) {
            lastBlock.appendChild(endContainer);
        }
        else if (children.length > 1) {
            this.addCursorMarker(endContainer);
            this.moveAllChildren(endContainer, lastBlock);
            endLi.insertBefore(lastBlock, endLi.firstChild);
        }
        if (children.length === 1) {
            this.addCursorMarker(endContainer);
            this.moveAllChildren(endContainer, firstBlock);
            if (endLi && endLi.parentNode) {
                endLi.parentNode.removeChild(endLi);
            }
        }
        let lastNewLi = null;
        if (startContainer.textContent.length > 0 && parentLi.textContent.length > 0) {
            this.moveAllChildren(firstBlock, startContainer);
            if (startContainer.nodeName === 'A') {
                startContainer = startContainer.parentNode.lastChild;
            }
        }
        else {
            const newLi = createElement('li');
            newLi.appendChild(firstBlock);
            fragment.appendChild(newLi);
            if (children.length === 1) {
                lastNewLi = newLi;
            }
        }
        if (isNOU(lastNewLi)) {
            lastNewLi = this.appendListItems(fragment, children, 1, children.length - 1);
        }
        if (isNOU(startContainer.nextSibling)) {
            parentList.insertBefore(fragment, parentLi.nextSibling);
        }
        else if (lastNewLi) {
            this.moveSiblingsToLiAndInsert(startContainer.nextSibling, lastNewLi, fragment, parentLi, parentList);
        }
        else {
            // nextSibling exists → insert before it
            parentList.insertBefore(fragment, startContainer.nextSibling);
        }
        if (parentLi.textContent === '' && parentLi.parentNode) {
            parentLi.parentNode.removeChild(parentLi);
        }
    }
    // Handles insertion for collapsed selection
    static pasteLI(insertedNode, parentLi, mergeNode, blockNode, range, nodeCutter) {
        let children = this.extractChildNodes(insertedNode);
        children = this.processInsertNodes(children);
        const blockNodeLength = this.getBlockNodeLength(blockNode);
        const parentList = parentLi.parentNode;
        let isCursorAtStart = true;
        let isCursorAtEnd = false;
        if (parentLi.contains(mergeNode) && mergeNode.previousSibling && mergeNode.previousSibling.textContent.trim().length !== 0) {
            isCursorAtStart = false;
        }
        const isAtStart = range.startOffset === 0 && isCursorAtStart;
        if (!isAtStart) {
            let parentLiLastChild = parentLi.lastChild;
            while (!isNOU(parentLiLastChild) && parentLiLastChild.nodeType === Node.ELEMENT_NODE &&
                !this.isBlockElement(parentLiLastChild) && !isNOU(parentLiLastChild.lastChild)) {
                parentLiLastChild = parentLiLastChild.lastChild;
            }
            if (range.startContainer === parentLiLastChild &&
                range.startContainer.textContent.length === range.startOffset) {
                isCursorAtEnd = true;
            }
            else if (parentLi.contains(mergeNode) && (isNOU(mergeNode.nextSibling) || mergeNode.nextSibling && ['LI', 'UL', 'OL'].indexOf(mergeNode.nextSibling.nodeName) !== -1) && range.startOffset === mergeNode.textContent.length) {
                let previousSib = mergeNode.previousSibling;
                let textLength = range.startOffset;
                while (previousSib && previousSib.nodeName !== 'LI') {
                    textLength += previousSib.textContent.length;
                    previousSib = previousSib.previousSibling;
                }
                isCursorAtEnd = textLength === blockNodeLength;
            }
        }
        const isAtEnd = range.startOffset === blockNodeLength || isCursorAtEnd;
        if (isAtStart) {
            this.handlePasteAtStart(children, parentLi, mergeNode, parentList);
        }
        else if (isAtEnd) {
            this.handlePasteAtEnd(children, parentLi, mergeNode, parentList);
        }
        else {
            this.handlePasteInMiddle(children, parentLi, mergeNode, range, parentList, nodeCutter);
        }
        this.unwrapInlineWrappers(parentList);
    }
    // Handles insertion at start
    static handlePasteAtStart(children, parentLi, mergeNode, parentList) {
        const lastBlock = children[children.length - 1];
        this.addCursorMarker(mergeNode);
        this.moveAllChildren(mergeNode, lastBlock);
        parentLi.insertBefore(lastBlock, parentLi.firstChild);
        const fragment = this.createLiFragment(children, 0, children.length - 1); // exclude last
        parentList.insertBefore(fragment, parentLi);
    }
    // Handles insertion at end
    static handlePasteAtEnd(children, parentLi, mergeNode, parentList) {
        const firstBlock = children[0];
        const hasNestedList = this.hasNestedListInsideLi(mergeNode);
        if (mergeNode.nodeName === 'LI' && hasNestedList) {
            const movedNodes = this.collectAndRemoveFollowingNodes(parentLi, hasNestedList);
            this.moveAllChildren(firstBlock, mergeNode);
            movedNodes.forEach((node) => mergeNode.appendChild(node));
        }
        else {
            this.moveAllChildren(firstBlock, mergeNode);
        }
        const fragment = this.createLiFragment(children, 1, children.length); // exclude first
        const lastNewLi = fragment.lastChild;
        let cursorNode = parentLi;
        if (isNOU(lastNewLi)) {
            cursorNode = parentLi;
        }
        else {
            cursorNode = lastNewLi;
        }
        while (!isNOU(cursorNode.lastChild) && cursorNode.lastChild.nodeName !== '#text') {
            cursorNode = cursorNode.lastChild;
        }
        this.addCursorMarker(cursorNode, true);
        if (lastNewLi) {
            const movedNodes = this.collectAndRemoveFollowingNodes(parentLi, hasNestedList ? hasNestedList : mergeNode.nextSibling);
            movedNodes.forEach((node) => lastNewLi.appendChild(node));
            this.insertFragmentAfterLi(fragment, parentLi, parentList);
        }
    }
    // Handles insertion in middle
    static handlePasteInMiddle(children, parentLi, mergeNode, range, parentList, nodeCutter) {
        const middleLi = nodeCutter.GetSpliceNode(range, mergeNode);
        const preNode = middleLi.previousSibling;
        const lastNode = middleLi.nextSibling;
        const firstBlock = children[0];
        if (children.length === 1) {
            this.addCursorMarker(lastNode);
            this.moveAllChildren(lastNode, firstBlock);
        }
        this.moveAllChildren(firstBlock, preNode);
        const fragment = this.createLiFragment(children, 1, children.length); // exclude first
        const lastNewLi = fragment.lastChild;
        if (lastNewLi) {
            this.addCursorMarker(lastNode);
            if (lastNode.nodeName === 'A') {
                lastNewLi.lastChild.appendChild(lastNode);
            }
            else {
                this.mergeLastNodeContent(lastNode, lastNewLi);
            }
        }
        const hasNestedList = this.hasNestedListInsideLi(parentLi);
        if ((lastNode && isNOU(lastNode.nextSibling) && lastNewLi && isNOU(hasNestedList)) || lastNode.nodeName === 'LI') {
            parentList.insertBefore(fragment, parentLi.nextSibling);
            if (lastNode.textContent.length === 0) {
                lastNode.parentNode.removeChild(lastNode);
            }
        }
        else if (lastNewLi) {
            const movedNodes = this.collectAndRemoveFollowingNodes(parentLi, hasNestedList ? hasNestedList : lastNode);
            movedNodes.forEach((node) => lastNewLi.appendChild(node));
            this.insertFragmentAfterLi(fragment, parentLi, parentList);
        }
        middleLi.parentNode.removeChild(middleLi);
    }
    // Checks if there is any nested list inside li
    static hasNestedListInsideLi(node) {
        if (node.nodeName === 'LI') {
            for (const child of Array.from(node.children)) {
                if (child.tagName === 'UL' || child.tagName === 'OL') {
                    return child;
                }
            }
        }
        const closestLi = node.closest('LI');
        if (!closestLi) {
            return null;
        }
        for (const child of Array.from(closestLi.children)) {
            if (child.tagName === 'UL' || child.tagName === 'OL') {
                return child;
            }
        }
        return null;
    }
    // Returns the length of block node
    static getBlockNodeLength(blockNode) {
        if (blockNode.nodeName === 'LI') {
            let length = 0;
            for (const child of Array.from(blockNode.childNodes)) {
                if (child.nodeType === Node.ELEMENT_NODE && ['UL', 'OL'].indexOf(child.tagName) !== -1) {
                    break;
                }
                length += child.textContent ? child.textContent.length : 0;
            }
            return length;
        }
        return blockNode.textContent ? blockNode.textContent.length : 0;
    }
    // Adds cursor marker
    static addCursorMarker(lastNode, isEnd) {
        const span = createElement('span');
        span.className = 'paste-cursor';
        if (isEnd) {
            lastNode.appendChild(span);
        }
        else {
            lastNode.insertBefore(span, lastNode.firstChild);
        }
    }
    // Checks if list item has another list
    static extractNestedListsIntoNewListItem(listItem) {
        const childNodes = Array.from(listItem.childNodes);
        const listNodes = [];
        // Find ul/ol nodes
        for (const node of childNodes) {
            if (node.nodeType === Node.ELEMENT_NODE &&
                (node.tagName === 'UL' || node.tagName === 'OL')) {
                listNodes.push(node);
            }
        }
        if (listNodes.length > 0) {
            // Create a new <li>
            const newLi = createElement('li');
            // Move ul/ol into the new <li>
            for (const list of listNodes) {
                newLi.appendChild(list);
            }
            // Insert new <li> after mergeNode
            const parent = listItem.parentNode;
            if (parent) {
                const next = listItem.nextSibling;
                if (next) {
                    parent.insertBefore(newLi, next);
                }
                else {
                    parent.appendChild(newLi);
                }
            }
        }
    }
    // Creates a fragment of list items
    static createLiFragment(nodes, start, end) {
        const fragment = document.createDocumentFragment();
        if (nodes.length === 2 && start === 1 &&
            (nodes[1].nodeName === 'UL' || nodes[1].nodeName === 'OL')) {
            // execute this code only for nested list paste use case.
            fragment.appendChild(nodes[1]);
        }
        else {
            for (let i = start; i < end; i++) {
                const node = nodes[i];
                if (node.nodeName === 'UL' || node.nodeName === 'OL') {
                    // snapshot childNodes to avoid live-collection mutation
                    const children = Array.from(node.childNodes);
                    for (const child of children) {
                        if (this.isBlockElement(child)) {
                            fragment.appendChild(child.cloneNode(true));
                        }
                    }
                }
                else {
                    const li = createElement('li');
                    li.appendChild(nodes[i]);
                    fragment.appendChild(li);
                }
            }
        }
        return fragment;
    }
    // Collects and removes following nodes
    static collectAndRemoveFollowingNodes(parentLi, startNode) {
        const nodes = [];
        let current = startNode;
        while (current) {
            const next = current.nextSibling;
            nodes.push(current);
            if (parentLi.contains(current)) {
                parentLi.removeChild(current);
            }
            current = next;
        }
        return nodes;
    }
    // Inserts fragment after list item
    static insertFragmentAfterLi(fragment, parentLi, parentList) {
        if (parentLi.nextSibling) {
            parentList.insertBefore(fragment, parentLi.nextSibling);
        }
        else {
            parentLi.appendChild(fragment);
        }
    }
    // Moves all children
    static moveAllChildren(sourceNode, targetNode) {
        const isAnchorInTargetNode = targetNode.nodeName === 'A';
        const isAnchorInSourceNode = sourceNode.nodeName === 'A';
        while (sourceNode.firstChild && !isAnchorInSourceNode) {
            const firstChild = sourceNode.firstChild;
            if (isAnchorInTargetNode) {
                targetNode.parentNode.insertBefore(firstChild, targetNode.nextSibling);
                targetNode = targetNode.nextSibling;
            }
            else {
                targetNode.appendChild(firstChild);
            }
        }
        if (isAnchorInSourceNode) {
            targetNode.appendChild(sourceNode);
        }
    }
    // Merges last node content
    static mergeLastNodeContent(lastNode, lastNewLi) {
        while (lastNode && lastNode.firstChild) {
            const firstChild = lastNode.firstChild;
            if (!firstChild) {
                continue;
            }
            const isBlockTag = CONSTANT.BLOCK_TAGS.indexOf(firstChild.nodeName.toLowerCase()) >= 0;
            if (!isBlockTag && lastNewLi.lastChild && lastNewLi.lastChild.nodeType === Node.ELEMENT_NODE) {
                lastNewLi.lastChild.appendChild(firstChild);
            }
            else if (firstChild.nodeName === 'UL' || firstChild.nodeName === 'OL') {
                lastNewLi.appendChild(firstChild);
            }
            else {
                this.moveAllChildren(firstChild, lastNewLi.lastChild);
                lastNode.removeChild(firstChild);
            }
        }
    }
    // Gets the appropriate node splice element based on selection and context.
    static getSplitElementForInsertion(range, nodeCutter, blockNode) {
        const isSelectionCollapsed = range.collapsed;
        const isAtNodeStart = range.startOffset === 0;
        const isAtNodeEnd = range.startContainer.nodeType === Node.TEXT_NODE ?
            range.startOffset === range.startContainer.textContent.length :
            range.startOffset === range.startContainer.childNodes.length;
        if (blockNode &&
            blockNode.nodeName === 'P' &&
            (isAtNodeStart || isAtNodeEnd || !isSelectionCollapsed) &&
            blockNode.textContent.trim() === '') {
            // Use a single split for empty paragraphs or paragraphs with only cursor position.
            return nodeCutter.SplitNode(range, blockNode, true);
        }
        else {
            // Use full GetSpliceNode for other cases.
            return nodeCutter.GetSpliceNode(range, blockNode);
        }
    }
    // Adjusts the cursor position post-insertion to ensure it is placed at the correct point.
    static cursorPos(lastSelectionNode, insertedNode, nodeSelection, docElement, editNode) {
        lastSelectionNode.classList.add('lastNode');
        editNode.innerHTML = updateTextNode(editNode.innerHTML, this.isBlazor);
        lastSelectionNode = editNode.querySelector('.lastNode');
        if (!isNOU(lastSelectionNode)) {
            this.placeCursorEnd(lastSelectionNode, insertedNode, nodeSelection, docElement, editNode);
            lastSelectionNode.classList.remove('lastNode');
            if (lastSelectionNode.classList.length === 0) {
                lastSelectionNode.removeAttribute('class');
            }
        }
    }
    // Handles focus management specifically for image/audio/video elements during insertion operations.
    static mediaFocus(node, nodeSelection, docElement) {
        const focusNode = document.createTextNode('\u00A0');
        const wrapper = node.nodeType === Node.ELEMENT_NODE ? node.closest('.e-audio-wrap, .e-video-wrap') : null;
        if (node.parentNode && node.parentNode.nodeName === 'A') {
            const anchorTag = node.parentNode;
            const parentNode = anchorTag.parentNode;
            parentNode.insertBefore(focusNode, anchorTag.nextSibling);
            parentNode.insertBefore(node, focusNode);
        }
        else if (node.nodeName === 'IMG' && (!node.nextSibling ||
            (node.nextSibling && node.nextSibling.nodeName === '#text' && node.nextSibling.textContent !== '\u00A0'))) {
            node.parentNode.insertBefore(focusNode, node.nextSibling);
        }
        else if (!isNOU(wrapper) && (node.nodeName === 'VIDEO' || node.nodeName === 'AUDIO')) {
            wrapper.parentNode.insertBefore(focusNode, wrapper.nextSibling);
            nodeSelection.setSelectionText(docElement, focusNode, focusNode, 0, 0);
            return;
        }
        nodeSelection.setSelectionText(docElement, node.nextSibling, node.nextSibling, 0, 0);
    }
    // Identifies the immediate block-level node, utilized for placement and alignment logic.
    // eslint-disable-next-line
    static getImmediateBlockNode(node, editNode) {
        while (node && CONSTANT.BLOCK_TAGS.indexOf(node.nodeName.toLocaleLowerCase()) < 0) {
            node = node.parentNode;
        }
        return node;
    }
    // Eliminates comments from a node to ensure the insertion is clean and comment-free.
    static removingComments(insertedNode) {
        let innerElement = insertedNode.innerHTML;
        innerElement = innerElement.replace(/<!--[\s\S]*?-->/g, '');
        insertedNode.innerHTML = innerElement;
    }
    // Finds and detaches empty elements from the DOM.
    static findDetachEmptyElem(element, ignoreBlockNodes = false) {
        let removableElement;
        if (!isNOU(element.parentElement)) {
            const hasNbsp = element.parentElement.textContent.length > 0 && element.parentElement.textContent.match(/\u00a0/g)
                && element.parentElement.textContent.match(/\u00a0/g).length > 0;
            const hasBr = !isNOU(element.parentElement.querySelector('br'));
            if (!hasNbsp && !hasBr && element.parentElement.textContent.trim() === '' && element.parentElement.contentEditable !== 'true' &&
                isNOU(element.parentElement.querySelector('img')) && element.parentElement.nodeName !== 'TD' && element.parentElement.nodeName !== 'TH' && isNOU(element.parentElement.querySelector('table td, table th'))) {
                removableElement = ignoreBlockNodes && CONSTANT.BLOCK_TAGS.indexOf(element.parentElement.tagName.toLowerCase()) !== -1 ?
                    element : this.findDetachEmptyElem(element.parentElement, ignoreBlockNodes);
            }
            else {
                removableElement = ignoreBlockNodes && CONSTANT.BLOCK_TAGS.indexOf(element.tagName.toLowerCase()) !== -1 ? null :
                    element;
            }
        }
        else {
            removableElement = null;
        }
        return removableElement;
    }
    // Removes elements deemed empty if isolated.
    static removeEmptyElements(element, ignoreBlockNodes = false, emptyElemet = null) {
        const emptyElements = element.querySelectorAll(':empty');
        const filteredEmptyElements = Array.from(emptyElements).filter((element) => {
            const tagName = element.tagName.toLowerCase();
            // Some empty tags suc as TD TH convey a meaning and hence should not be removed.
            const meaningfulEmptyTags = ['td', 'tr', 'th', 'textarea', 'input', 'img', 'video', 'audio', 'br', 'hr', 'iframe'];
            return !element.closest('svg') && !element.closest('canvas') && !(meaningfulEmptyTags.indexOf(tagName) > -1);
        });
        for (let i = 0; i < filteredEmptyElements.length; i++) {
            let lineWithDiv = true;
            const currentEmptyElem = filteredEmptyElements[i];
            if (currentEmptyElem.tagName === 'DIV') {
                lineWithDiv = currentEmptyElem.style.borderBottom === 'none' ||
                    currentEmptyElem.style.borderBottom === '' ? true : false;
            }
            if (currentEmptyElem.nodeName === 'COL') {
                if (!currentEmptyElem.style.width) {
                    const colGroup = currentEmptyElem.parentElement;
                    detach(colGroup);
                }
                continue;
            }
            const isEmptyElement = !isNOU(emptyElemet) && currentEmptyElem === emptyElemet;
            if (CONSTANT.SELF_CLOSING_TAGS.indexOf(currentEmptyElem.tagName.toLowerCase()) < 0 && lineWithDiv && !isEmptyElement) {
                const detachableElement = this.findDetachEmptyElem(currentEmptyElem, ignoreBlockNodes);
                if (!isNOU(detachableElement) && !(detachableElement.nodeType === Node.ELEMENT_NODE && detachableElement.nodeName.toUpperCase() === 'TEXTAREA')) {
                    detach(detachableElement);
                }
            }
        }
    }
    // Finds the most relevant parent element considered in operations like insertion or cleanup.
    static findClosestRelevantElement(sourceElement, editNode) {
        // Cast to Element type for proper handling.
        let currentElement = sourceElement;
        // First check if the element is inside a table or list item.
        const relevantAncestorTags = ['table', 'li'];
        for (const ancestorTag of relevantAncestorTags) {
            const closestAncestorElement = closest(currentElement, ancestorTag);
            if (closestAncestorElement && !closestAncestorElement.contains(editNode)) {
                return closestAncestorElement;
            }
        }
        // Traverse up the DOM tree until we reach a valid parent or run out of elements.
        while (currentElement && currentElement.nodeType === Node.ELEMENT_NODE) {
            const parentElement = currentElement.parentNode;
            if (parentElement === editNode) {
                return currentElement;
            }
            // Check if parent is one of the allowed block elements.
            const isParentTagValid = !isNOU(parentElement.tagName) && (this.isTagInList(parentElement.tagName, CONSTANT.IGNORE_BLOCK_TAGS) ||
                this.isTagInList(parentElement.tagName, CONSTANT.ALLOWED_TABLE_BLOCK_TAGS));
            if (isParentTagValid) {
                return currentElement;
            }
            // Move up to the parent element.
            currentElement = parentElement;
        }
        return null;
    }
    // Determines if a provided tag matches any entries in a given list of permissible tags.
    static isTagInList(tagName, tagList) {
        return tagList.indexOf(tagName.toLowerCase()) !== -1;
    }
    // Facilitates the insertion of a table within a list structure, reorganizing elements as needed.
    static insertTableInList(range, insertNode, parentNode, currentNode, nodeCutter, lastclosestParentNode) {
        const parentList = closest(parentNode, 'ul,ol');
        const totalLi = parentList ? parentList.querySelectorAll('li').length : 0;
        const preNode = nodeCutter.SplitNode(range, parentNode, true);
        const sibNode = preNode.previousElementSibling;
        // Get next sibling info for potential content movement.
        const nextSibNode = lastclosestParentNode ? closest(lastclosestParentNode, 'li') : null;
        const nextSibNodeInitialHTML = nextSibNode ? nextSibNode.innerHTML : null;
        // Determine if we have a valid previous sibling in a list with more items than the original.
        const hasSiblingInLargerList = sibNode && closest(sibNode, 'ol,ul') &&
            closest(sibNode, 'ol,ul').querySelectorAll('li').length > totalLi;
        if (hasSiblingInLargerList) {
            // Insert table inside previous sibling and move content there.
            sibNode.appendChild(insertNode);
            range.deleteContents();
            // Move content from preNode to sibNode if needed.
            if (preNode.childNodes.length > 0) {
                this.moveChildNodes(preNode, sibNode);
            }
            // Handle content movement from next sibling if necessary.
            const nextSiblingContentChanged = parentNode !== lastclosestParentNode &&
                nextSibNodeInitialHTML && nextSibNodeInitialHTML !== nextSibNode.innerHTML;
            if (nextSiblingContentChanged) {
                this.moveChildNodes(nextSibNode, sibNode);
            }
        }
        else {
            // Insert table at beginning of current node.
            range.deleteContents();
            preNode.insertBefore(insertNode, preNode.firstChild);
            // Move content if needed.
            if (parentNode !== lastclosestParentNode) {
                this.moveChildNodes(lastclosestParentNode, parentNode);
            }
        }
        // Clean up and mark table.
        this.removeEmptyNextLI(closest(insertNode, 'li'));
        insertNode.classList.add('ignore-table');
    }
    // Transfers child nodes from a source to target element to symmetrically manage content.
    static moveChildNodes(source, target) {
        while (!isNOU(source) && !isNOU(source.firstChild)) {
            target.appendChild(source.firstChild);
        }
    }
    // Checks and adjusts text alignment in elements affected by new content insertions.
    static alignCheck(editNode) {
        const spanAligns = editNode.querySelectorAll('span[style*="text-align"]');
        for (let i = 0; i < spanAligns.length; i++) {
            const spanAlign = spanAligns[i];
            if (spanAlign) {
                const blockAlign = this.getImmediateBlockNode(spanAlign, null);
                if (blockAlign) {
                    let totalSpanText = '';
                    for (let j = 0; j < spanAligns.length; j++) {
                        const span = spanAligns[j];
                        if (blockAlign.contains(span)) {
                            totalSpanText += span.textContent;
                        }
                    }
                    if (blockAlign.textContent.trim() === totalSpanText.trim()) {
                        blockAlign.style.textAlign = spanAlign.style.textAlign;
                    }
                }
            }
        }
    }
    // Removes list structures from the pasted content, cleaning up unnecessary list items.
    static removeListfromPaste(range) {
        range.deleteContents();
        const value = range.startContainer;
        if (!isNOU(value) && value.nodeName === 'LI' && !isNOU(value.parentElement) && (value.parentElement.nodeName === 'OL' || value.parentElement.nodeName === 'UL') && value.textContent.trim() === '') {
            value.parentElement.querySelectorAll('li').forEach((item) => {
                if (item.textContent.trim() === '' && item !== value) {
                    item.remove();
                }
            });
        }
    }
    // Check if we're inserting a horizontal rule in an empty block element
    static isHorizontalRuleInEmptyBlock(node, range) {
        if (node.nodeName !== 'HR') {
            return null;
        }
        let container = range.startContainer;
        while (!this.isBlockElement(container) && !isNOU(container.parentElement)) {
            container = container.parentElement;
        }
        const isTextContentPresent = container.textContent.trim() !== '';
        const isMediaElementPresent = !isNOU(container.querySelector('video')) ||
            !isNOU(container.querySelector('audio')) || !isNOU(container.querySelector('img'));
        return !(isTextContentPresent || isMediaElementPresent) ? container : null;
    }
    // Check if the node is a media element
    static isMediaElement(node) {
        if (node) {
            return node.nodeName === 'VIDEO' || node.nodeName === 'AUDIO';
        }
        return false;
    }
    // Method to insert block elements correctly in a list structure
    static insertBlockElementInList(range, insertNode, parentNode, nodeCutter) {
        const parentList = closest(parentNode, 'ul,ol');
        const totalListItems = parentList ? parentList.querySelectorAll('li').length : 0;
        const newListNode = nodeCutter.SplitNode(range, parentNode, true);
        const currentListNode = newListNode.previousElementSibling;
        // Insert the block element before the list or inside it based on context
        if (currentListNode && parentList && parentList.querySelectorAll('li').length > totalListItems) {
            currentListNode.appendChild(insertNode.firstChild);
            if (newListNode.childNodes.length > 0) {
                this.moveChildNodes(newListNode, currentListNode);
            }
        }
        else {
            if (newListNode.firstChild && newListNode.firstChild.nodeName === 'HR') {
                newListNode.insertBefore(insertNode.firstChild, newListNode.firstChild.nextSibling);
            }
            else {
                newListNode.insertBefore(insertNode.firstChild, newListNode.firstChild);
            }
        }
        // Cleanup and ensure the block element is set correctly
        if (newListNode.textContent.trim() === '' && newListNode.childNodes.length < 1) {
            detach(newListNode);
        }
    }
}
InsertHtml.inlineNode = ['a', 'abbr', 'acronym', 'audio', 'b', 'bdi', 'bdo', 'big', 'br', 'button',
    'canvas', 'cite', 'code', 'data', 'datalist', 'del', 'dfn', 'em', 'embed', 'font', 'i', 'iframe', 'img', 'input',
    'ins', 'kbd', 'label', 'map', 'mark', 'meter', 'noscript', 'object', 'output', 'picture', 'progress',
    'q', 'ruby', 's', 'samp', 'script', 'select', 'slot', 'small', 'span', 'strong', 'sub', 'sup', 'svg',
    'template', 'textarea', 'time', 'title', 'u', 'tt', 'var', 'video', 'wbr'];
InsertHtml.formattingInlineNodes = ['span', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'code',
    'sub', 'sup'];
InsertHtml.contentsDeleted = false;
InsertHtml.isBlazor = false;
InsertHtml.isAnotherLiFromEndLi = false;
